import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useBoardChartData } from '../../hooks/useBoardChartData';
import { formatCurrency, getCurrencySymbol, CHART_COLORS, CHART_STYLES, formatCompactNumber } from '../charts/chartTheme';

import {
    ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { Target, TrendingUp, Info, X, LineChart, Pause, Package, Activity, BarChart3, RefreshCw } from 'lucide-react';

// ============================================================
// FORECAST MODULE IMPORTS
//
// All calculation logic lives in the forecast/ directory.
// This component is purely UI — it consumes computed datasets
// and renders them as charts.
//
// See forecast/index.ts for the full module architecture.
// ============================================================
import {
    // Types
    RoasPoint,
    NonPaidPoint,
    RoasForecastResult,
    NonPaidForecastResult,
    ForecastChartPoint,
    ExpenseForecastResult,
    ExpensePoint,
    JournalChart,
    SpecChart,

    // Engines (Stage 1 & 2: Historical + Forecast)
    computeRoasForecast,
    computeNonPaidForecast,
    computeUnknownSourceForecast,
    computeExpenseForecast,

    // Combination Engine (Stage 3: Modulation + Aggregation)
    applyRoasModulators,
    applyNonPaidModulators,
    computeModulatedCombinedRoas,
    computeModulatedTotalRevenue,
    computeModulatedCombinedNonPaid,
    computeRevenueForecast,
    computeStatisticalAccuracy,
    computeJournalCharts,
    computeSpecCharts,
    calculateRevenueSourceBreakdown,

    // UI Components
    ProductExplorer,
    SourceIcon
} from './forecast';
import { BoardBottomDrawer } from '../BoardBottomDrawer';

import { useBoardStore } from '../../../../stores/boardStore';
import { boardService } from '../../../../lib/api/boards';
import { JournalOperation } from './forecast';
import { BoardEntity } from '../../../../stores/boardStore';
import { findCampaignMatch } from '../../lib/OriginResolver';
import { AIVerdictModule } from '../../components/AIVerdictModule';
// BoardLoader removed as it's now handled by the parent BoardMainSpace


interface ForecastPageProps {
    isEditMode?: boolean;
}

import { SafeImage } from '../../../../components/common/SafeImage';
import { motion, AnimatePresence } from 'framer-motion';
import { format as formatDate, isYesterday } from 'date-fns';

/** Elegant empty state shown when a forecast tab has insufficient data */
const ForecastEmptyState = React.memo(({ title, description }: { title?: string; description?: string }) => {
    const { t } = useTranslation();
    return (
        <div className="h-[200px] w-full flex flex-col items-center justify-center gap-3 relative font-['Inter',system-ui,sans-serif]">
            {/* Subtle dotted grid background */}
            <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.06]" style={{
                backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
                backgroundSize: '16px 16px'
            }} />
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="relative flex flex-col items-center gap-2.5"
            >
                <div className="relative">
                    <div className="w-10 h-10 rounded-xl bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 flex items-center justify-center shadow-sm">
                        <BarChart3 className="w-5 h-5 text-accent-primary" />
                    </div>
                    <motion.div
                        className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-accent-primary border-2 border-white dark:border-neutral-900"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    />
                </div>
                <div className="text-center">
                    <p className="text-xs font-bold text-text-primary dark:text-neutral-100 tracking-tight">
                        {title || t('pages.forecast.empty.notEnoughData', 'Not Enough Data')}
                    </p>
                    <p className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-1 max-w-[220px] leading-relaxed">
                        {description || t('pages.forecast.empty.needMoreHistory', 'We need more order history for this product to generate a reliable forecast.')}
                    </p>
                </div>
            </motion.div>
        </div>
    );
});

/** Premium full-page empty state for the entire forecast module */
const ForecastPageEmptyState = React.memo(({ t, title, description, hideRefresh, onAction, actionLabel }: { t: any; title?: string; description?: string; hideRefresh?: boolean; onAction?: () => void; actionLabel?: string }) => (
    <div className="flex-1 min-h-[60vh] flex flex-col items-center justify-center p-8 text-center bg-white dark:bg-neutral-900 overflow-hidden relative font-['Inter',system-ui,sans-serif]">
        {/* Subtle grid background matched with standard empty states */}
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.07] pointer-events-none" style={{
            backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
            backgroundSize: '24px 24px'
        }} />
        
        <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative z-10 max-w-sm w-full flex flex-col items-center"
        >
            <div className="w-16 h-16 bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-200 dark:border-neutral-700 flex items-center justify-center mb-6 text-accent-primary shadow-sm">
                <BarChart3 className="w-8 h-8" />
            </div>

            <h3 className="text-lg font-bold text-text-primary dark:text-neutral-100 mb-1.5 tracking-tight">
                {title || t('pages.forecast.empty.title', 'Gathering Historical Data')}
            </h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6 leading-relaxed px-4">
                {description || t('pages.forecast.empty.description', 'Forecasting requires a baseline of historical performance. Connect your store and marketing channels to start visualizing your future patterns.')}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 w-full justify-center px-4">
                {onAction && actionLabel && (
                    <button 
                        onClick={onAction}
                        className="flex items-center justify-center gap-2 px-6 py-2 bg-accent-primary text-white rounded-xl font-bold text-sm hover:brightness-110 transition-all active:scale-95 shadow-md shadow-accent-primary/10"
                    >
                        {actionLabel}
                    </button>
                )}
                {!hideRefresh && (
                    <button 
                        onClick={() => window.location.reload()}
                        className="flex items-center justify-center gap-2 px-6 py-2 bg-white dark:bg-neutral-800 text-text-primary dark:text-neutral-100 border border-neutral-200 dark:border-neutral-700 rounded-xl font-bold text-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-all active:scale-95 shadow-sm"
                    >
                        <RefreshCw className="w-4 h-4" />
                        {t('actions.refresh', 'Refresh Dashboard')}
                    </button>
                )}
            </div>
            
            <div className="mt-8 flex items-center gap-4 py-2 px-4 rounded-full bg-white dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 shadow-sm">
                <div className="flex -space-x-1.5">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="w-5 h-5 rounded-full border-2 border-white dark:border-neutral-900 bg-accent-primary/20 animate-pulse" style={{ animationDelay: `${i * 0.15}s` }} />
                    ))}
                </div>
                <span className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-widest">
                    {t('pages.forecast.empty.monitoring', 'Monitoring Signals')}
                </span>
            </div>
        </motion.div>
    </div>
));

const PHASE_METADATA: Record<string, { labelKey: string, color: string, icon: any }> = {
    learning: { labelKey: 'pages.forecast.phases.learning', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Activity },
    scaling: { labelKey: 'pages.forecast.phases.scaling', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400', icon: TrendingUp },
    stabilizing: { labelKey: 'pages.forecast.phases.stabilizing', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: Target },
    sunset: { labelKey: 'pages.forecast.phases.sunset', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', icon: TrendingUp },
    testing: { labelKey: 'pages.forecast.phases.testing', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', icon: Info },
    paused: { labelKey: 'pages.forecast.phases.paused', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: Pause },
};

const SOURCE_PALETTE = [
    '#10b981', // emerald-500
    '#3b82f6', // blue-500
    '#6366f1', // indigo-500
    '#f43f5e', // rose-500
    '#f59e0b', // amber-500
    '#8b5cf6', // violet-500
    '#ec4899', // pink-500
    '#06b6d4', // cyan-500
];

const ForecastDistributionBar = ({ segments, formatCurrency, currencySymbol }: any) => {
    const [hoveredName, setHoveredName] = React.useState<string | null>(null);

    if (!segments || segments.length === 0) return null;

    return (
        <div className="relative mb-6">
            <div className="h-[14px] w-full bg-neutral-100 dark:bg-neutral-800 rounded overflow-hidden flex shadow-inner">
                    {segments.map((source: any, idx: number) => {
                        const isHighlighted = hoveredName === source.name;
                        return (
                            <div
                                key={`${source.name}-${idx}`}
                                style={{
                                    width: `${source.attribution}%`,
                                    backgroundColor: source.color
                                }}
                                className={`h-full group relative cursor-pointer transition-all duration-300 border-r border-white/10 last:border-0 flex items-center justify-center ${isHighlighted ? 'brightness-110 z-[10]' : ''}`}
                                onMouseEnter={() => setHoveredName(source.name)}
                                onMouseLeave={() => setHoveredName(null)}
                            >
                                {source.attribution > 6 && (
                                    <div className="flex items-center gap-0.5 opacity-95 group-hover:opacity-100 transition-opacity pointer-events-none px-0.5 overflow-hidden">
                                        <SourceIcon name={source.name} className="w-[9px] h-[9px] shrink-0 text-white" />
                                        {source.attribution > 18 && (
                                            <span className="text-[8px] font-black text-white uppercase tracking-tighter truncate leading-none">
                                                {source.name}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Tooltips Overlay */}
                {segments.map((source: any, idx: number, arr: any[]) => {
                    const isHovered = hoveredName === source.name;
                    let prevCumulative = 0;
                    for (let i = 0; i < idx; i++) prevCumulative += arr[i].attribution;
                    const center = prevCumulative + source.attribution / 2;

                    let shift = -50;
                    if (center < 15) shift = -50 + (15 - center) * 3;
                    else if (center > 85) shift = -50 - (center - 85) * 3;
                    const finalShift = Math.max(-100, Math.min(0, shift));

                    return (
                        <div
                            key={`tooltip-${source.name}-${idx}`}
                            style={{ left: `${center}%` }}
                            className={`absolute top-0 bottom-0 pointer-events-none ${isHovered ? 'z-[100]' : 'z-[40]'}`}
                        >
                            <div
                                style={{ transform: `translateX(${finalShift}%)` }}
                                className={`absolute transition-all duration-300 ${isHovered ? 'opacity-100 -top-10' : 'opacity-0 -top-8'} whitespace-nowrap`}
                            >
                                <div className="bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 text-[9px] font-black uppercase tracking-[0.2em] px-4 py-2 rounded shadow-2xl border border-white/20 dark:border-black/10 flex items-center gap-2">
                                    <SourceIcon name={source.name} className="w-2.5 h-2.5 text-white dark:text-neutral-900" />
                                    <span>{source.name}: {source.attribution.toFixed(1)}% ({formatCurrency(source.revenue, currencySymbol)})</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
    );
};

const RevenueSourceCards = React.memo(({ 
    targets,
    currencySymbol,
    formatCurrency,
    t,
    activeRevenueSource,
    onTargetSelect
}: any) => {
    if (targets.length === 0) return null;

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {targets.map((target: any, idx: number) => {
                const hexColor = target.color || SOURCE_PALETTE[idx % SOURCE_PALETTE.length];
                const isActive = activeRevenueSource === (target.isPaid ? `${target.name} - Paid` : target.name);
                
                return (
                    <motion.div
                        key={`${target.name}-${target.isPaid ? 'paid' : 'organic'}`}
                        onClick={() => typeof onTargetSelect === 'function' && onTargetSelect(isActive ? null : (target.isPaid ? `${target.name} - Paid` : target.name))}
                        className={`bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm hover:shadow-md flex flex-col cursor-pointer overflow-hidden transition-all duration-300 p-4 ${isActive ? 'ring-2 ring-accent-primary ring-inset' : ''}`}
                    >
                        {/* Mobile Layout (Horizontal) */}
                        <div className="md:hidden flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                <SourceIcon 
                                    name={target.name} 
                                    className="w-4 h-4 shrink-0" 
                                    color={hexColor}
                                />
                                <div className="flex flex-col min-w-0 flex-1">
                                    <h3 className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider truncate">{target.name}</h3>
                                    <div className="flex mt-0.5">
                                        <span className={`px-1 py-0.5 rounded text-[7px] font-black uppercase tracking-tighter ${target.isPaid ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'}`}>
                                            {target.isPaid ? t('pages.traffic.table.paid') : t('pages.traffic.table.organic')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="text-sm font-black text-neutral-900 dark:text-neutral-100 shrink-0">
                                {formatCurrency(target.revenue, currencySymbol)}
                            </div>
                        </div>

                        {/* Desktop Layout */}
                        <div className="hidden md:flex flex-col">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2 overflow-hidden">
                                    <SourceIcon 
                                        name={target.name} 
                                        className="w-4 h-4 shrink-0" 
                                        color={hexColor}
                                    />
                                    <h3 className="text-[11px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider truncate">{target.name}</h3>
                                </div>
                                <span className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-tighter shrink-0 ${target.isPaid ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-amber-400'}`}>
                                    {target.isPaid ? t('pages.traffic.table.paid') : t('pages.traffic.table.organic')}
                                </span>
                            </div>
                            <div className="text-xl font-black text-neutral-900 dark:text-neutral-100 truncate">
                                {formatCurrency(target.revenue, currencySymbol)}
                            </div>
                            <div className={`mt-2 flex items-center gap-1 text-[10px] font-medium opacity-80`} style={{ color: hexColor }}>
                                <Activity className="w-3 h-3" />
                                {t('common.forecast', 'Forecast')}
                            </div>
                        </div>
                    </motion.div>
                );
            })}
        </div>
    );
});

const ACCURACY_INFO: Record<string, { titleKey: string; bodyKey: string }> = {
    backtest: {
        titleKey: "pages.forecast.accuracy.backtest.title",
        bodyKey: "pages.forecast.accuracy.backtest.body"
    },
    quality: {
        titleKey: "pages.forecast.accuracy.quality.title",
        bodyKey: "pages.forecast.accuracy.quality.body"
    },
    fidelity: {
        titleKey: "pages.forecast.accuracy.fidelity.title",
        bodyKey: "pages.forecast.accuracy.fidelity.body"
    },
    horizon: {
        titleKey: "pages.forecast.accuracy.horizon.title",
        bodyKey: "pages.forecast.accuracy.horizon.body"
    },
    coverage: {
        titleKey: "pages.forecast.accuracy.coverage.title",
        bodyKey: "pages.forecast.accuracy.coverage.body"
    },
    sources: {
        titleKey: "pages.forecast.accuracy.sources.title",
        bodyKey: "pages.forecast.accuracy.sources.body"
    }
};

const ForecastKpiCards = React.memo(({
    activeKpiView,
    setActiveKpiView,
    forecastedTotal,
    currencySymbol,
    accuracyScore,
    formatCurrency,
    selectedProduct,
    setSelectedProduct,
    t
}: any) => {
    return (
        <>
            <motion.div
                className={`bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm cursor-pointer overflow-hidden flex flex-col transition-all duration-300 p-4 ${activeKpiView === 'revenue' ? 'ring-2 ring-accent-primary ring-inset' : ''}`}
                onClick={() => {
                    setActiveKpiView(activeKpiView === 'revenue' ? null : 'revenue');
                    if (selectedProduct) setSelectedProduct(null);
                }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-success-500 bg-opacity-10 dark:bg-success-900/30">
                            <TrendingUp className="w-4 h-4 text-success-600 dark:text-success-400" />
                        </div>
                        <h3 className="text-xs font-bold text-neutral-500 uppercase tracking-widest">{t('pages.forecast.kpis.forecastedRevenue', 'Forecasted Revenue')}</h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <motion.div
                            initial={{ opacity: 0, x: 10 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="text-sm font-black text-neutral-900 dark:text-neutral-100 truncate"
                        >
                            {formatCurrency(forecastedTotal, currencySymbol)}
                        </motion.div>
                    </div>
                </div>
            </motion.div>

            <motion.div
                className={`bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-sm flex flex-col cursor-pointer overflow-hidden transition-all duration-300 p-4 ${activeKpiView === 'accuracy' ? 'ring-2 ring-accent-primary ring-inset' : ''}`}
                onClick={() => {
                    setActiveKpiView(activeKpiView === 'accuracy' ? null : 'accuracy');
                    if (selectedProduct) setSelectedProduct(null);
                }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-accent-primary bg-opacity-10 dark:bg-accent-primary/20">
                            <Activity className="w-4 h-4 text-accent-primary" />
                        </div>
                        <h3 className="text-xs font-bold text-neutral-500 uppercase tracking-widest">{t('pages.forecast.kpis.statisticalAccuracy', 'Statistical Accuracy')}</h3>
                    </div>
                    <div className="flex items-center gap-2">
                        <motion.div
                            initial={{ opacity: 0, x: 10 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="text-sm font-black text-neutral-900 dark:text-neutral-100 truncate"
                        >
                            {accuracyScore.toFixed(1)}%
                        </motion.div>
                    </div>
                </div>
            </motion.div>
        </>
    );
});

// ============================================================
// MEMOIZED UI COMPONENTS (Moved outside to prevent re-creation)
// ============================================================

// OLD SLIDER COMPONENTS REMOVED

const RoasChartBlock = React.memo(({
    data, title, stepNum, color, sourceKey, halfLife, halfLifeSource, backtestScore, phase, hideSliders = false, roasForecastData, optValue = 50, noiseValue = 100
}: {
    data: RoasPoint[], title: string, stepNum: number, color: string, sourceKey: string,
    halfLife?: number, halfLifeSource?: string, backtestScore?: number | null,
    phase?: any, hideSliders?: boolean, roasForecastData: RoasForecastResult,
    optValue?: number, noiseValue?: number
}) => {
    const { t } = useTranslation();
    const srcData = roasForecastData.perSourceCharts.find(s => s.id === sourceKey);
    const displayData = hideSliders ? data : applyRoasModulators(data, optValue, noiseValue, srcData?.avgSpend || 0, srcData?.avgSpend, srcData?.platform);
    const safeId = sourceKey.replace(/[^a-zA-Z0-9]/g, '');
    return (
        <div className="bg-white dark:bg-neutral-900 rounded-xl p-5 border border-neutral-200 dark:border-neutral-800 shadow-sm flex flex-col min-h-[320px] w-full">
            <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black text-white" style={{ backgroundColor: color }}>{stepNum}</div>
                    <h3 className="font-bold text-sm text-neutral-800 dark:text-neutral-200">{title}</h3>
                </div>
                <div className="flex items-center gap-2">
                    {phase && (
                        <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold ${PHASE_METADATA[phase.current]?.color || ''}`}>
                            {t(PHASE_METADATA[phase.current]?.labelKey) || phase.current}
                        </div>
                    )}
                    {halfLife !== undefined && (
                        <span className="text-[9px] px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-500 font-semibold">
                            HL: {halfLife.toFixed(0)}d ({halfLifeSource})
                        </span>
                    )}
                    {backtestScore !== undefined && backtestScore !== null && (
                        <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${backtestScore > 70 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : backtestScore > 40 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                            Backtest: {backtestScore.toFixed(0)}%
                        </span>
                    )}
                </div>
            </div>
            <div className="flex-1 w-full min-h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={displayData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                        <defs>
                            <linearGradient id={`conf-${safeId}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={color} stopOpacity={0.04} />
                                <stop offset="95%" stopColor={color} stopOpacity={0.005} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-default, #E5E7EB)" />
                        <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                        <YAxis tickFormatter={(v) => v.toFixed(1) + 'x'} tick={{ fontSize: 9, fill: '#6B7280' }} width={40} axisLine={false} tickLine={false} />
                        <Tooltip formatter={(value: any, name: string) => {
                            if (value === null) return ['-', name];
                            return [Number(value).toFixed(2) + 'x', name === 'actualRoas' ? 'Actual ROAS' : name === 'forecastRoas' ? 'Predicted ROAS' : name === 'upperRoas' ? 'Upper Bound' : name === 'lowerRoas' ? 'Lower Bound' : name];
                        }} contentStyle={{ ...CHART_STYLES.tooltip.contentStyle, fontSize: '11px' }} />
                        <Area type="monotone" dataKey="upperRoas" stroke="none" fill={`url(#conf-${safeId})`} isAnimationActive={false} />
                        <Area type="monotone" dataKey="lowerRoas" stroke="none" fill="var(--bg-card, #ffffff)" isAnimationActive={false} />
                        <Line type="monotone" dataKey="actualRoas" stroke={color} strokeWidth={2} dot={{ r: 0 }} connectNulls={true} isAnimationActive={false} />
                        <Line type="monotone" dataKey="forecastRoas" stroke={color} strokeWidth={2} strokeDasharray="4 4" dot={{ r: 0 }} connectNulls={true} strokeOpacity={0.8} isAnimationActive={false} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
            {/* OLD NOISE SLIDER REMOVED */}
            {phase && (
                <div className="mt-2 text-[8px] text-neutral-400 italic font-medium px-1">
                    AI Context: {phase.reason}
                </div>
            )}
        </div>
    );
});
const NonPaidChartBlock = React.memo(({ data, title, stepNum, color, evolutionLabel, backtestScore, currencySymbol, optValue = 50, noiseValue = 100 }: {
    data: NonPaidPoint[], title: string, stepNum: number, color: string, evolutionLabel?: string,
    backtestScore?: number | null, currencySymbol: string,
    optValue?: number, noiseValue?: number
}) => {
    const { t } = useTranslation();
    const displayData = applyNonPaidModulators(data, optValue, noiseValue);
    const safeId = title.replace(/[^a-zA-Z0-9]/g, '');
    return (
        <div className="bg-white dark:bg-neutral-900 rounded-xl p-5 border border-neutral-200 dark:border-neutral-800 shadow-sm flex flex-col min-h-[320px] w-full">
            <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black text-white" style={{ backgroundColor: color }}>{stepNum}</div>
                    <h3 className="font-bold text-sm text-neutral-800 dark:text-neutral-200">{title}</h3>
                </div>
                <div className="flex items-center gap-2">
                    {evolutionLabel && (
                        <span className="text-[9px] px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800 text-neutral-500 font-semibold">
                            {evolutionLabel}
                        </span>
                    )}
                    {backtestScore !== undefined && backtestScore !== null && (
                        <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${backtestScore > 70 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : backtestScore > 40 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                            Backtest: {backtestScore.toFixed(0)}%
                        </span>
                    )}
                </div>
            </div>
            <div className="flex-1 w-full min-h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={displayData} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                        <defs>
                            <linearGradient id={`np-conf-${safeId}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={color} stopOpacity={0.04} />
                                <stop offset="95%" stopColor={color} stopOpacity={0.005} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-default, #E5E7EB)" />
                        <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                        <YAxis tickFormatter={(v) => formatCompactNumber(v, currencySymbol)} tick={{ fontSize: 9, fill: '#6B7280' }} width={50} axisLine={false} tickLine={false} />
                        <Tooltip formatter={(value: any, name: string) => {
                            if (value === null) return ['-', name];
                            return [
                                formatCurrency(Number(value), currencySymbol),
                                name === 'actual' ? t('pages.forecast.charts.actualRevenue', 'Actual Revenue') : 
                                name === 'forecast' ? t('pages.forecast.charts.forecastedRevenue', 'Forecasted Revenue') : 
                                name === 'upper' ? t('pages.forecast.charts.upperBound', 'Upper Bound') : 
                                name === 'lower' ? t('pages.forecast.charts.lowerBound', 'Lower Bound') : name
                            ];
                        }} contentStyle={{ ...CHART_STYLES.tooltip.contentStyle, fontSize: '11px' }} />
                        <Area type="monotone" dataKey="upper" stroke="none" fill={`url(#np-conf-${safeId})`} isAnimationActive={false} />
                        <Area type="monotone" dataKey="lower" stroke="none" fill="var(--bg-card, #ffffff)" isAnimationActive={false} />
                        <Line type="monotone" dataKey="actual" stroke={color} strokeWidth={2} dot={{ r: 0 }} connectNulls={true} isAnimationActive={false} />
                        <Line type="monotone" dataKey="forecast" stroke={color} strokeWidth={2} strokeDasharray="4 4" dot={{ r: 0 }} connectNulls={true} strokeOpacity={0.8} isAnimationActive={false} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
            {/* OLD NOISE SLIDER REMOVED */}
        </div>
    );
});

const GenericForecastChartBlock = React.memo(({ data, title, stepNum, color, subtitle, currencySymbol }: { data: ForecastChartPoint[], title: string, stepNum: string | number, color: string, subtitle?: string, currencySymbol: string }) => {
    const { t } = useTranslation();
    return (
        <div className="bg-white dark:bg-neutral-900 rounded-xl p-5 border border-neutral-200 dark:border-neutral-800 shadow-sm flex flex-col min-h-[320px] w-full">
            <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black text-white" style={{ backgroundColor: color }}>{stepNum}</div>
                    <div className="flex flex-col">
                        <h3 className="font-bold text-sm text-neutral-800 dark:text-neutral-200">{title}</h3>
                        {subtitle && <span className="text-[9px] text-neutral-500 font-medium uppercase tracking-tighter">{subtitle}</span>}
                    </div>
                </div>
            </div>
            <div className="flex-1 w-full min-h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-default, #E5E7EB)" />
                        <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                        <YAxis tickFormatter={(v) => formatCompactNumber(v, currencySymbol)} tick={{ fontSize: 9, fill: '#6B7280' }} width={50} axisLine={false} tickLine={false} />
                        <Tooltip formatter={(value: any, name: string) => {
                            if (value === null) return ['-', name];
                            return [formatCurrency(Number(value), currencySymbol),
                            name === 'actual' ? t('pages.forecast.charts.actualRevenue', 'Actual Revenue') : name === 'forecast' ? t('pages.forecast.charts.forecastedRevenue', 'Forecasted Revenue') : name === 'upper' ? 'Upper Bound' : name === 'lower' ? 'Lower Bound' : name];
                        }} contentStyle={{ ...CHART_STYLES.tooltip.contentStyle, fontSize: '11px' }} />
                        <Area type="monotone" dataKey="upper" stroke="none" fill={color} fillOpacity={0.05} isAnimationActive={false} />
                        <Area type="monotone" dataKey="lower" stroke="none" fill="var(--bg-card, #ffffff)" isAnimationActive={false} />
                        <Line type="monotone" dataKey="actual" stroke={color} strokeWidth={2.5} dot={{ r: 0 }} connectNulls={true} isAnimationActive={false} />
                        <Line type="monotone" dataKey="forecast" stroke={color} strokeWidth={2.5} strokeDasharray="5 5" dot={{ r: 0 }} connectNulls={true} isAnimationActive={false} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
});

const CombinedRevenueChart = React.memo(({ data, title, stepNum, currencySymbol }: { data: any[], title: string, stepNum: number, currencySymbol: string }) => {
    const { t } = useTranslation();
    return (
        <div className="bg-white dark:bg-neutral-900 rounded-xl p-5 border border-primary-100 dark:border-primary-900/30 shadow-sm flex flex-col min-h-[320px] w-full bg-gradient-to-br from-white to-primary-50/20 dark:from-neutral-900 dark:to-primary-900/5">
            <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black text-white bg-primary-600">{stepNum}</div>
                    <div className="flex flex-col">
                        <h3 className="font-bold text-sm text-neutral-800 dark:text-neutral-200">{title}</h3>
                        <span className="text-[9px] text-primary-500 font-medium uppercase tracking-tighter">{t('pages.forecast.charts.bottomUpTransformation', 'Bottom-Up Transformation')}</span>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 bg-primary-500/10 px-2 py-0.5 rounded-full">
                    <TrendingUp className="w-3 h-3 text-primary-500" />
                    <span className="text-[9px] font-bold text-primary-600 dark:text-primary-400">{t('pages.forecast.charts.accurateSummation', 'Accurate Summation')}</span>
                </div>
            </div>
            <div className="flex-1 w-full min-h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                        <defs>
                            <linearGradient id="revenueSumConf" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.05} />
                                <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0.005} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-default, #E5E7EB)" />
                        <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                        <YAxis tickFormatter={(v) => formatCompactNumber(v, currencySymbol)} tick={{ fontSize: 9, fill: '#6B7280' }} width={50} axisLine={false} tickLine={false} />
                        <Tooltip formatter={(value: any, name: string) => {
                            if (value === null) return ['-', name];
                            return [
                                formatCurrency(Number(value), currencySymbol), 
                                name === 'actual' ? t('pages.forecast.charts.actualRevenue', 'Actual Revenue') : 
                                name === 'forecast' ? t('pages.forecast.charts.predictedRevenue', 'Predicted Revenue') : 
                                name === 'upper' ? t('pages.forecast.charts.upperBound', 'Upper Bound') : 
                                name === 'lower' ? t('pages.forecast.charts.lowerBound', 'Lower Bound') : name
                            ];
                        }} contentStyle={{ ...CHART_STYLES.tooltip.contentStyle, fontSize: '11px' }} />
                        <Area type="monotone" dataKey="upper" stroke="none" fill="url(#revenueSumConf)" isAnimationActive={false} />
                        <Area type="monotone" dataKey="lower" stroke="none" fill="var(--bg-card, #ffffff)" isAnimationActive={false} />
                        <Line type="monotone" dataKey="actual" stroke={CHART_COLORS.profit} strokeWidth={2.5} dot={{ r: 0 }} connectNulls={true} isAnimationActive={false} />
                        <Line type="monotone" dataKey="forecast" stroke={CHART_COLORS.primary} strokeWidth={2.5} strokeDasharray="5 5" dot={{ r: 0 }} connectNulls={true} isAnimationActive={false} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
            <div className="mt-2 pt-2 border-t border-neutral-100 dark:border-neutral-800">
                <p className="text-[10px] text-neutral-400 leading-tight">
                    {t('pages.forecast.charts.revenueTransformationDesc', 'Generated by transforming per-source ROAS targets into revenue based on manual spend targets and organic trends. Reflects all optimism and volatility adjustments.')}
                </p>
            </div>
        </div>
    );
});

const ExpenseForecastChart = React.memo(({ data, title, stepNum, currencySymbol }: { data: ExpensePoint[], title: string, stepNum: string | number, currencySymbol: string }) => {
    const { t } = useTranslation();
    return (
        <div className="bg-white dark:bg-neutral-900 rounded-xl p-5 border border-rose-100 dark:border-rose-900/30 shadow-sm flex flex-col min-h-[320px] w-full bg-gradient-to-br from-white to-rose-50/20 dark:from-neutral-900 dark:to-rose-900/5">
            <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black text-white bg-rose-600">{stepNum}</div>
                    <div className="flex flex-col">
                        <h3 className="font-bold text-sm text-neutral-800 dark:text-neutral-200">{title}</h3>
                        <span className="text-[9px] text-rose-500 font-medium uppercase tracking-tighter">{t('pages.forecast.charts.totalCostOperations', 'Total Cost of Operations')}</span>
                    </div>
                </div>
            </div>
            <div className="flex-1 w-full min-h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                        <defs>
                            <linearGradient id="expenseSumConf" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.04} />
                                <stop offset="95%" stopColor="#f43f5e" stopOpacity={0.005} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-default, #E5E7EB)" />
                        <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                        <YAxis tickFormatter={(v) => formatCompactNumber(v, currencySymbol)} tick={{ fontSize: 9, fill: '#6B7280' }} width={50} axisLine={false} tickLine={false} />
                        <Tooltip formatter={(value: any, name: string) => {
                            if (value === null) return ['-', name];
                            return [
                                formatCurrency(Number(value), currencySymbol),
                                name === 'actual' ? t('pages.forecast.charts.actualExpense', 'Actual Expense') :
                                name === 'forecast' ? t('pages.forecast.charts.predictedExpense', 'Predicted Expense') :
                                name === 'upper' ? t('pages.forecast.charts.upperBound', 'Upper Bound') :
                                name === 'lower' ? t('pages.forecast.charts.lowerBound', 'Lower Bound') : name
                            ];
                        }} contentStyle={{ ...CHART_STYLES.tooltip.contentStyle, fontSize: '11px' }} />
                        <Area type="monotone" dataKey="upper" stroke="none" fill="url(#expenseSumConf)" isAnimationActive={false} />
                        <Area type="monotone" dataKey="lower" stroke="none" fill="var(--bg-card, #ffffff)" isAnimationActive={false} />
                        <Line type="monotone" dataKey="actual" stroke="#f43f5e" strokeWidth={2.5} dot={{ r: 0 }} connectNulls={true} isAnimationActive={false} />
                        <Line type="monotone" dataKey="forecast" stroke="#f43f5e" strokeWidth={2.5} strokeDasharray="5 5" dot={{ r: 0 }} connectNulls={true} isAnimationActive={false} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
            <div className="mt-2 pt-2 border-t border-neutral-100 dark:border-neutral-800">
                <p className="text-[10px] text-neutral-400 leading-tight">
                    {t('pages.forecast.charts.expenseAggregateDesc', 'Aggregate of all marketing spend, journal entries, calculated specs (taxes/fees), and operational general expenses. Includes seasonality and adaptive jitter for realism.')}
                </p>
            </div>
        </div>
    );
});

const NonPaidForecastSection = React.memo(({ nonPaidForecastData, currencySymbol, getNpOptimism, getNpVolatility }: {
    nonPaidForecastData: NonPaidForecastResult,
    currencySymbol: string,
    getNpOptimism: (key: string) => number,
    getNpVolatility: (key: string) => number
}) => {
    const { t } = useTranslation();
    if (!nonPaidForecastData.charts || nonPaidForecastData.charts.length === 0) return null;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-neutral-500" />
                    <h3 className="text-xs font-bold text-neutral-500 uppercase tracking-widest">{t('pages.forecast.sections.nonPaidRevenue', 'Non-Paid Revenue Forecasts')}</h3>
                </div>
            </div>

            <div className={`grid gap-4 ${nonPaidForecastData.charts.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                {nonPaidForecastData.charts.map((src, idx) => {
                    return (
                        <NonPaidChartBlock
                            key={src.source}
                            data={src.data}
                            title={src.source}
                            stepNum={idx + 1}
                            color={src.color}
                            evolutionLabel={src.evolutionLabel}
                            backtestScore={src.backtestScore}
                            currencySymbol={currencySymbol}
                            optValue={getNpOptimism(`np-${src.source}`)}
                            noiseValue={getNpVolatility(`np-${src.source}`)}
                        />
                    );
                })}
            </div>
        </div>
    );
});

export const ForecastPage: React.FC<ForecastPageProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings, entities, channels, journals, specs, dateRange } = useBoardChartData();
    const boardId = useBoardStore(state => state.boardId);

    const [journalOperations, setJournalOperations] = useState<JournalOperation[]>([]);

    React.useEffect(() => {
        if (!boardId || !journals.length) return;
        const fetchAllOps = async () => {
            try {
                const results = await Promise.all(
                    journals.map(j => boardService.getJournalOperations(boardId, j.id))
                );
                setJournalOperations(results.flat());
            } catch (err) {
                console.error("Failed to fetch journal operations", err);
            }
        };
        fetchAllOps();
    }, [boardId, journals]);

    const currencySymbol = getCurrencySymbol(currencySettings?.default);
    const [forecastDays, setForecastDays] = useState<7 | 30 | 90>(30);
    const [infoModal, setInfoModal] = useState<{ title: string; body: string } | null>(null);

    const [activeKpiView, setActiveKpiView] = useState<'revenue' | 'accuracy' | null>(null);
    const [selectedRevenueSource, setSelectedRevenueSource] = useState<string | null>(null);
    const [isAiMobileExpanded, setIsAiMobileExpanded] = useState<boolean>(false);
    const [selectedProduct, setSelectedProduct] = useState<BoardEntity | null>(null);
    const selectedProductId = selectedProduct?.id || null;

    // Feature flag for debug charts
    const isDebugEnabled = process.env.ENABLE_DEBUG === 'true';
    const [showDebugCharts, setShowDebugCharts] = useState<boolean>(false);


    // ============================================================
    // SLIDER STATE
    // ============================================================

    // Equalizer sliders (global revenue modifiers)
    const eqGlobal = {
        traffic: 100, conversion: 100, aov: 100, retention: 100, refundRate: 100
    };

    // Defaults for engines (Automated via Jitter Profiles)
    const getOptimism = (key: string) => roasForecastData?.jitterProfiles?.[key]?.defaultOptimism ?? 50;
    const getVolatility = (key: string) => roasForecastData?.jitterProfiles?.[key]?.defaultSliderValue ?? 100;
    const getNpOptimism = (key: string) => nonPaidForecastData?.npJitterProfiles?.[key]?.defaultOptimism ?? 50;
    const getNpVolatility = (key: string) => nonPaidForecastData?.npJitterProfiles?.[key]?.defaultSliderValue ?? 100;
    const getUnkOptimism = (_key?: string) => unknownForecastData?.chart?.jitterProfile?.defaultOptimism ?? 50;
    const getUnkVolatility = (_key?: string) => unknownForecastData?.chart?.jitterProfile?.defaultSliderValue ?? 100;

    const marginPercent = 30; // Hardcoded default



    // ============================================================
    // STAGE 1 + 2: ROAS FORECAST (Historical + Per-Source Forecast)
    //
    // Computes:
    //   - roasForecastData.perSourceCharts  → individual ROAS forecasts per ad platform
    //   - roasForecastData.combinedChart    → spend-weighted blended ROAS (raw, pre-slider)
    //   - roasForecastData.backtestScores   → per-source accuracy scores
    //   - roasForecastData.jitterProfiles   → per-source noise profiles
    //
    // Dependencies: finance, entities, channels, forecastDays
    // ============================================================
    const roasForecastData = useMemo(() => computeRoasForecast(finance, entities, channels, forecastDays), [finance, entities, channels, forecastDays]);


    const marketingSpendAbsolute = useMemo(() => {
        const abs: Record<string, number> = {};
        if (roasForecastData?.perSourceCharts) {
            roasForecastData.perSourceCharts.forEach(src => {
                abs[src.id] = (src.avgSpend || 0);
            });
        }
        return abs;
    }, [roasForecastData]);

    // ============================================================
    // STAGE 1 + 2: NON-PAID FORECAST (Historical + Per-Source Forecast)
    //
    // Computes:
    //   - nonPaidForecastData.charts          → individual organic revenue forecasts
    //   - nonPaidForecastData.npJitterProfiles → per-source noise profiles
    //
    // Dependencies: finance, forecastDays, t (for translation)
    // ============================================================
    const nonPaidForecastData = useMemo(() => computeNonPaidForecast(finance, forecastDays, t('pages.traffic.modals.platforms.directOrganic', 'Direct / Organic')), [finance, forecastDays, t]);



    // ============================================================
    // STAGE 1 + 2: UNKNOWN SOURCES FORECAST
    // ============================================================
    const unknownForecastData = useMemo(() => computeUnknownSourceForecast(
        finance, 
        entities, 
        channels, 
        forecastDays, 
        t('pages.forecast.charts.unknownSourceRevenue', 'Unknown Source Revenue'),
        t('pages.forecast.charts.untrackedLabel', 'Untracked / Disconnected')
    ), [finance, entities, channels, forecastDays, t]);

    // PRODUCT FORECASTS MAP (For Product Explorer)
    // ============================================================


    const productForecastsMap = useMemo(() => {
        if (!finance || !entities) return {};

        const map: Record<string, number> = {};
        const allTxs = finance.transactions || [];

        const products = entities.filter(e => {
            const type = (e.entityType || '').toLowerCase();
            return !type.includes('ad') && !type.includes('set') && !type.includes('campaign') &&
                (type.includes('product') || type.includes('variant') || (e.feed?.assets && (e.feed.assets.high?.length > 0 || e.feed.assets.low?.length > 0)));
        });

        products.forEach(product => {
            const productId = String(product.id);
            const revenueTransactionIds = new Set<string>();
            const matchedAdEntityIds = new Set<string>();

            const revenueTxs = allTxs.filter((tx: any) => {
                const traffic = tx.traffic ? (typeof tx.traffic === 'string' ? JSON.parse(tx.traffic) : tx.traffic) : {};
                const txProdId = String(tx.originalEntityId || tx.entityId || '');
                if (txProdId === productId) return true;
                if (String(traffic.product_id) === productId || String(traffic.variant_id) === productId) return true;
                return false;
            });

            if (revenueTxs.length === 0) {
                map[productId] = 0;
                return;
            }

            revenueTxs.forEach((tx: any) => {
                if (tx.id) revenueTransactionIds.add(String(tx.id));
                const traffic = tx.traffic ? (typeof tx.traffic === 'string' ? JSON.parse(tx.traffic) : tx.traffic) : {};
                if (traffic.ad_id) matchedAdEntityIds.add(String(traffic.ad_id));
                if (traffic.adset_id) matchedAdEntityIds.add(String(traffic.adset_id));
                if (traffic.campaign_id) matchedAdEntityIds.add(String(traffic.campaign_id));
                if (traffic.utm_id) matchedAdEntityIds.add(String(traffic.utm_id));
                const sourceName = (tx.source || traffic.source || traffic.utm_source || '').toLowerCase();
                if (traffic.isPaid && sourceName) matchedAdEntityIds.add(sourceName);
            });

            const matchedEntityExternalIds = new Set<string>();
            entities.forEach((e: BoardEntity) => {
                if (matchedAdEntityIds.has(String(e.externalId))) {
                    matchedEntityExternalIds.add(String(e.externalId));
                    if (e.parentId) {
                        const parent = entities.find(p => String(p.id) === String(e.parentId));
                        if (parent?.externalId) matchedEntityExternalIds.add(String(parent.externalId));
                        if (parent?.parentId) {
                            const grandparent = entities.find(gp => String(gp.id) === String(parent.parentId));
                            if (grandparent?.externalId) matchedEntityExternalIds.add(String(grandparent.externalId));
                        }
                    }
                }
            });

            const expenseTxs = allTxs.filter((tx: any) => {
                if (tx.type !== 'EXPENSE') return false;
                const traffic = tx.traffic ? (typeof tx.traffic === 'string' ? JSON.parse(tx.traffic) : tx.traffic) : {};
                const adId = traffic.ad_id ? String(traffic.ad_id) : null;
                const adsetId = traffic.adset_id ? String(traffic.adset_id) : null;
                const campaignId = traffic.campaign_id ? String(traffic.campaign_id) : null;
                const utmId = traffic.utm_id ? String(traffic.utm_id) : null;
                const txEntityId = String(tx.originalEntityId || tx.entityId || '');

                if (adId && (matchedAdEntityIds.has(adId) || matchedEntityExternalIds.has(adId))) return true;
                if (adsetId && (matchedAdEntityIds.has(adsetId) || matchedEntityExternalIds.has(adsetId))) return true;
                if (campaignId && (matchedAdEntityIds.has(campaignId) || matchedEntityExternalIds.has(campaignId))) return true;
                if (utmId && (matchedAdEntityIds.has(utmId) || matchedEntityExternalIds.has(utmId))) return true;
                if (txEntityId && matchedEntityExternalIds.has(txEntityId)) return true;

                return false;
            });

            const productFinance = {
                ...finance,
                transactions: [...revenueTxs, ...expenseTxs],
            };

            const prodRoas = computeRoasForecast(productFinance, entities, channels, forecastDays);
            const prodNonPaid = computeNonPaidForecast(productFinance, forecastDays, t('pages.traffic.modals.platforms.directOrganic', 'Direct / Organic'));

            const revenueChartData = computeModulatedTotalRevenue(
                prodRoas,
                prodNonPaid,
                { traffic: 100, conversion: 100, aov: 100, retention: 100, refundRate: 100 },
                () => 50,
                () => 100,
                () => 50,
                () => 100,
                [],
                [],
                [],
                { chart: null },
                () => 50,
                () => 100,
                (prodRoas?.perSourceCharts || []).reduce((acc: any, src: any) => ({ ...acc, [src.id]: src.avgSpend || 0 }), {}),
                30
            );

            const fTotal = revenueChartData.filter((pt: any) => pt.isForecast).reduce((sum: number, pt: any) => sum + (pt.forecast || 0), 0);
            map[productId] = fTotal;
        });

        return map;
    }, [finance, entities, channels, forecastDays]);

    const aggregatedBreakdownTargets = useMemo(() => {
        const entityToTargets: Record<string, { total: number, targets: Record<string, number> }> = {};
        const organicToTargets: Record<string, { total: number, targets: Record<string, number> }> = {};

        const activities = finance?.transactions?.filter((tx: any) => tx.isActive !== false && tx.type === 'REVENUE') || [];
        activities.forEach((tx: any) => {
            const traffic = tx.traffic ? (typeof tx.traffic === 'string' ? JSON.parse(tx.traffic) : tx.traffic) : {};
            const targetName = traffic.target || t('pages.traffic.modals.platforms.directOrganic', { defaultValue: 'Direct / Organic' });
            const amount = Number(tx.amount) || 0;

            if (traffic.isPaid) {
                const matchedEntity = findCampaignMatch(traffic, entities);
                const targetEntityId = matchedEntity ? String(matchedEntity.id) : `unmatched-${targetName}`;
                
                if (!entityToTargets[targetEntityId]) entityToTargets[targetEntityId] = { total: 0, targets: {} };
                entityToTargets[targetEntityId].total += amount;
                entityToTargets[targetEntityId].targets[targetName] = (entityToTargets[targetEntityId].targets[targetName] || 0) + amount;
            } else {
                const sourceName = tx.source || traffic.utm_source || traffic.source || targetName;
                if (!organicToTargets[sourceName]) organicToTargets[sourceName] = { total: 0, targets: {} };
                organicToTargets[sourceName].total += amount;
                organicToTargets[sourceName].targets[targetName] = (organicToTargets[sourceName].targets[targetName] || 0) + amount;
            }
        });

        const forecastTargets: Record<string, { revenue: number, typeScore: number, color?: string }> = {};
        let totalForecastedRevenue = 0;

        const categorizeOrganic = (name: string) => {
            const lower = name.toLowerCase();
            if (lower.includes('email') || lower.includes('klaviyo') || lower.includes('mailchimp') || lower.includes('newsletter')) return 3;
            if (lower.includes('direct') || lower === 'unknown' || lower === 'direct / organic') return 4;
            return 2; 
        };

        roasForecastData?.perSourceCharts?.forEach((src: any) => {
            const modulatedData = applyRoasModulators(src.data, getOptimism(src.id), getVolatility(src.id), src.avgSpend, src.avgSpend, src.platform);
            const sourceForecastTotal = modulatedData.filter((pt: any) => pt.actualRoas === null)
                .reduce((sum: number, pt: any) => sum + ((pt.forecastRoas || 0) * src.avgSpend), 0);
            
            const split = entityToTargets[src.id];
            if (split && split.total > 0) {
                Object.entries(split.targets).forEach(([targetName, targetRev]) => {
                    const key = `${targetName}::paid`;
                    if (!forecastTargets[key]) forecastTargets[key] = { revenue: 0, typeScore: 1, color: src.color };
                    forecastTargets[key].revenue += sourceForecastTotal * (targetRev / split.total);
                    totalForecastedRevenue += sourceForecastTotal * (targetRev / split.total);
                });
            } else {
                const key = `${src.source}::paid`;
                if (!forecastTargets[key]) forecastTargets[key] = { revenue: 0, typeScore: 1, color: src.color };
                forecastTargets[key].revenue += sourceForecastTotal;
                totalForecastedRevenue += sourceForecastTotal;
            }
        });

        nonPaidForecastData?.charts?.forEach((src: any) => {
            const modulatedData = applyNonPaidModulators(src.data, getNpOptimism(`np-${src.source}`), getNpVolatility(`np-${src.source}`));
            const sourceForecastTotal = modulatedData.filter((pt: any) => pt.actual === null)
                .reduce((sum: number, pt: any) => sum + (pt.forecast || 0), 0);
            
            const split = organicToTargets[src.source];
            if (split && split.total > 0) {
                Object.entries(split.targets).forEach(([targetName, targetRev]) => {
                    const key = `${targetName}::organic`;
                    if (!forecastTargets[key]) forecastTargets[key] = { revenue: 0, typeScore: categorizeOrganic(targetName), color: src.color };
                    forecastTargets[key].revenue += sourceForecastTotal * (targetRev / split.total);
                    totalForecastedRevenue += sourceForecastTotal * (targetRev / split.total);
                });
            } else {
                const key = `${src.source}::organic`;
                if (!forecastTargets[key]) forecastTargets[key] = { revenue: 0, typeScore: categorizeOrganic(src.source), color: src.color };
                forecastTargets[key].revenue += sourceForecastTotal;
                totalForecastedRevenue += sourceForecastTotal;
            }
        });

        const aggregated: Record<string, any> = {};
        Object.entries(forecastTargets).forEach(([key, data]) => {
            const [name, type] = key.split('::');
            if (!aggregated[name]) {
                aggregated[name] = {
                    name,
                    isPaid: type === 'paid',
                    revenue: 0,
                    typeScore: data.typeScore,
                    color: data.color
                };
            }
            aggregated[name].revenue += data.revenue;
            if (type === 'paid') {
                aggregated[name].isPaid = true;
                aggregated[name].typeScore = Math.min(aggregated[name].typeScore, data.typeScore);
                if (data.color) aggregated[name].color = data.color;
            }
        });

        return Object.values(aggregated)
            .map(data => ({
                ...data,
                attribution: totalForecastedRevenue > 0 ? (data.revenue / totalForecastedRevenue) * 100 : 0
            }))
            .filter(t => t.revenue > 0)
            .sort((a, b) => {
                const scoreA = a.typeScore;
                const scoreB = b.typeScore;
                if (scoreA !== scoreB) return scoreA - scoreB;
                return b.revenue - a.revenue;
            });
    }, [finance, entities, channels, roasForecastData, nonPaidForecastData, getOptimism, getVolatility, getNpOptimism, getNpVolatility, t, dateRange]);

    const productForecastData = useMemo(() => {
        if (!selectedProductId || !finance) return null;

        // STEP 1: Find all REVENUE transactions for this product
        const allTxs = finance.transactions || [];
        const revenueTransactionIds = new Set<string>();
        const matchedAdEntityIds = new Set<string>();

        const revenueTxs = allTxs.filter((tx: any) => {
            const traffic = tx.traffic ? (typeof tx.traffic === 'string' ? JSON.parse(tx.traffic) : tx.traffic) : {};
            const txProdId = String(tx.originalEntityId || tx.entityId || '');

            // Match level 1: Transaction explicitly for this product
            if (txProdId === selectedProductId) return true;

            // Match level 2: Traffic metadata points to this product
            if (String(traffic.product_id) === selectedProductId ||
                String(traffic.variant_id) === selectedProductId) return true;

            return false;
        });

        // STEP 2: Collect all ad entity IDs (campaigns/ads/adsets) that drove revenue to this product.
        // This is needed because EXPENSE transactions are linked to ad entities, not products.
        revenueTxs.forEach((tx: any) => {
            if (tx.id) revenueTransactionIds.add(String(tx.id));
            const traffic = tx.traffic ? (typeof tx.traffic === 'string' ? JSON.parse(tx.traffic) : tx.traffic) : {};

            // Collect all ad hierarchy IDs from revenue transactions
            if (traffic.ad_id) matchedAdEntityIds.add(String(traffic.ad_id));
            if (traffic.adset_id) matchedAdEntityIds.add(String(traffic.adset_id));
            if (traffic.campaign_id) matchedAdEntityIds.add(String(traffic.campaign_id));
            if (traffic.utm_id) matchedAdEntityIds.add(String(traffic.utm_id));

            // Also match by source for unmatched paid traffic
            const sourceName = (tx.source || traffic.source || traffic.utm_source || '').toLowerCase();
            if (traffic.isPaid && sourceName) matchedAdEntityIds.add(sourceName);
        });

        // Also find board entities that match these ad IDs and collect their external IDs
        const matchedEntityExternalIds = new Set<string>();
        entities?.forEach((e: BoardEntity) => {
            if (matchedAdEntityIds.has(String(e.externalId))) {
                matchedEntityExternalIds.add(String(e.externalId));
                // Also add parent chain (ad → adset → campaign)
                if (e.parentId) {
                    const parent = entities.find(p => String(p.id) === String(e.parentId));
                    if (parent?.externalId) matchedEntityExternalIds.add(String(parent.externalId));
                    if (parent?.parentId) {
                        const grandparent = entities.find(gp => String(gp.id) === String(parent.parentId));
                        if (grandparent?.externalId) matchedEntityExternalIds.add(String(grandparent.externalId));
                    }
                }
            }
        });

        // STEP 3: Include EXPENSE transactions from the same ad entities
        const expenseTxs = allTxs.filter((tx: any) => {
            if (tx.type !== 'EXPENSE') return false;
            const traffic = tx.traffic ? (typeof tx.traffic === 'string' ? JSON.parse(tx.traffic) : tx.traffic) : {};

            // Match expense to any ad entity that drove revenue to this product
            const adId = traffic.ad_id ? String(traffic.ad_id) : null;
            const adsetId = traffic.adset_id ? String(traffic.adset_id) : null;
            const campaignId = traffic.campaign_id ? String(traffic.campaign_id) : null;
            const utmId = traffic.utm_id ? String(traffic.utm_id) : null;
            const txEntityId = String(tx.originalEntityId || tx.entityId || '');

            if (adId && (matchedAdEntityIds.has(adId) || matchedEntityExternalIds.has(adId))) return true;
            if (adsetId && (matchedAdEntityIds.has(adsetId) || matchedEntityExternalIds.has(adsetId))) return true;
            if (campaignId && (matchedAdEntityIds.has(campaignId) || matchedEntityExternalIds.has(campaignId))) return true;
            if (utmId && (matchedAdEntityIds.has(utmId) || matchedEntityExternalIds.has(utmId))) return true;
            if (txEntityId && matchedEntityExternalIds.has(txEntityId)) return true;

            return false;
        });

        // Combine revenue + expense transactions
        const filteredTransactions = [...revenueTxs, ...expenseTxs];

        // Create a temporary finance object with ONLY these transactions
        const productFinance = {
            ...finance,
            transactions: filteredTransactions,
        };

        const prodRoas = computeRoasForecast(productFinance, entities, channels, forecastDays);
        const prodNonPaid = computeNonPaidForecast(productFinance, forecastDays, t('pages.traffic.modals.platforms.directOrganic', 'Direct / Organic'));

        return { roas: prodRoas, nonPaid: prodNonPaid };
    }, [selectedProductId, finance, entities, channels, forecastDays]);

    // UNKNOWN SOURCES SLIDER LOGIC REMOVED

    // ============================================================
    // STAGE 3a: MODULATED COMBINED ROAS
    //
    // The combined ROAS chart with individual slider adjustments
    // applied to each per-source forecast before spend-weighted averaging.
    //
    // Dependencies: roasForecastData, optimism, volatility
    // ============================================================
    const modulatedCombinedChart = useMemo(
        () => {
            const abs: Record<string, number> = {};
            if (roasForecastData?.perSourceCharts) {
                roasForecastData.perSourceCharts.forEach(src => {
                    abs[src.id] = (src.avgSpend || 0);
                });
            }
            return computeModulatedCombinedRoas(roasForecastData, getOptimism, getVolatility, abs);
        },
        [roasForecastData]
    );

    // ============================================================
    // STAGE 3b: MODULATED TOTAL REVENUE (Bottom-Up Summation)
    //
    // Combines paid (ROAS × spend) + non-paid revenue into a
    // single total revenue chart, reflecting all slider adjustments.
    //
    // Dependencies: roasForecastData, nonPaidForecastData, all sliders
    // ============================================================
    const modulatedTotalRevenueChart = useMemo(
        () => computeModulatedTotalRevenue(
            roasForecastData,
            nonPaidForecastData,
            eqGlobal,
            getOptimism,
            getVolatility,
            getNpOptimism,
            getNpVolatility,
            journals,
            journalOperations,
            specs,
            unknownForecastData,
            getUnkOptimism,
            getUnkVolatility,
            marketingSpendAbsolute
        ),
        [roasForecastData, nonPaidForecastData, journals, journalOperations, specs, unknownForecastData, marketingSpendAbsolute]
    );

    const expenseForecastData: ExpenseForecastResult = useMemo(
        () => computeExpenseForecast(
            finance,
            forecastDays,
            marketingSpendAbsolute,
            journals,
            journalOperations,
            specs,
            roasForecastData,
            nonPaidForecastData,
            entities,
            channels
        ),
        [finance, forecastDays, marketingSpendAbsolute, journals, journalOperations, specs, roasForecastData, nonPaidForecastData, entities, channels]
    );

    // ============================================================
    // STAGE 3c: MODULATED COMBINED NON-PAID REVENUE
    //
    // Sums all individual non-paid source forecasts into a single
    // aggregated organic revenue chart, reflecting slider adjustments.
    //
    // Dependencies: nonPaidForecastData, npOptimism, npVolatility
    // ============================================================
    const modulatedCombinedNonPaidChart = useMemo(
        () => computeModulatedCombinedNonPaid(nonPaidForecastData, getNpOptimism, getNpVolatility),
        [nonPaidForecastData]
    );

    const revenueForecastChart = useMemo(
        () => computeRevenueForecast(
            roasForecastData,
            nonPaidForecastData,
            getOptimism,
            getVolatility,
            getNpOptimism,
            getNpVolatility,
            journals,
            journalOperations,
            specs,
            unknownForecastData,
            getUnkOptimism,
            getUnkVolatility,
            marketingSpendAbsolute,
            marginPercent
        ),
        [roasForecastData, nonPaidForecastData, unknownForecastData, journals, journalOperations, specs, marketingSpendAbsolute, marginPercent]
    );

    // --- Sub-components (extracted for mobile drawer) ---

    const ProductStats = ({ selectedProductId, productForecastData, entities, currencySymbol, isMobile, computeModulatedTotalRevenue, CHART_STYLES, formatCurrency, t }: any) => {
        const [activeTab, setActiveTab] = useState<string>('forecast');
        const [activeSubTab, setActiveSubTab] = useState<string | null>(null);

        const product = entities.find((e: any) => e.id === selectedProductId);
        const productImg = (() => {
            if (!product?.feed?.assets) return null;
            const { assets } = product.feed;
            return assets.low?.[0] || assets.high?.[0];
        })();



        const tabs = useMemo(() => {
            const baseTabs = [{ id: 'forecast', label: t('pages.forecast.product.revenueForecast', 'Revenue Forecast'), type: 'combined' }];

            // Group paid sources by platform
            const paidGroups: Record<string, any[]> = {};
            (productForecastData.roas?.perSourceCharts || []).forEach((src: any) => {
                const grpKey = (src.platform || 'Other').toUpperCase();
                if (!paidGroups[grpKey]) paidGroups[grpKey] = [];
                paidGroups[grpKey].push({
                    id: `paid-${src.id}`,
                    label: src.source || grpKey,
                    type: 'paid',
                    source: src
                });
            });

            // Group organic sources by source name
            const organicGroups: Record<string, any[]> = {};
            (productForecastData.nonPaid?.charts || []).forEach((src: any) => {
                const grpKey = src.source;
                if (!organicGroups[grpKey]) organicGroups[grpKey] = [];
                organicGroups[grpKey].push({
                    id: `organic-${src.source}`,
                    label: src.source,
                    type: 'organic',
                    source: src
                });
            });

            const processedPaid = Object.entries(paidGroups).map(([platform, children]) => {
                if (children.length === 1) {
                    return { ...children[0], label: platform };
                }
                return {
                    id: `group-paid-${platform}`,
                    label: platform,
                    type: 'group',
                    children
                };
            });

            const processedOrganic = Object.entries(organicGroups).map(([source, children]) => {
                if (children.length === 1) {
                    return children[0];
                }
                return {
                    id: `group-organic-${source}`,
                    label: source,
                    type: 'group',
                    children
                };
            });

            return [...baseTabs, ...processedPaid, ...processedOrganic];
        }, [productForecastData]);

        // Sync activeTab when it becomes invalid
        React.useEffect(() => {
            if (activeTab !== 'forecast' && !tabs.some(t => t.id === activeTab)) {
                setActiveTab('forecast');
            }
        }, [tabs, activeTab]);

        // Sync activeSubTab when activeTab changes
        React.useEffect(() => {
            const currentTab = tabs.find(t => t.id === activeTab);
            if (currentTab?.type === 'group') {
                if (!activeSubTab || !currentTab.children.some((c: any) => c.id === activeSubTab)) {
                    setActiveSubTab(currentTab.children[0].id);
                }
            } else {
                setActiveSubTab(null);
            }
        }, [activeTab, tabs]);

        const activeTabData = useMemo(() => {
            const tab = tabs.find(t => t.id === activeTab) || tabs[0];
            if (tab.type === 'group') {
                if (activeSubTab) {
                    return tab.children.find((c: any) => c.id === activeSubTab) || tab.children[0];
                }
                return tab.children[0];
            }
            return tab;
        }, [tabs, activeTab, activeSubTab]);

        return (
            <div className={`${isMobile ? 'p-0 pb-6' : 'p-6 bg-white dark:bg-neutral-950 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-xl'} overflow-hidden relative`}>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 overflow-hidden flex-shrink-0">
                            {productImg ? (
                                <SafeImage src={productImg} alt={product?.name} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-neutral-300">
                                    <Package className="w-4 h-4 opacity-30" />
                                </div>
                            )}
                        </div>
                        <h4 className="font-bold text-sm text-neutral-800 dark:text-neutral-200 truncate pr-4">
                            {t('pages.forecast.product.stats', { name: product?.name || 'Product', defaultValue: `${product?.name || 'Product'} Stats` })}
                        </h4>
                    </div>
                </div>

                {/* Tabs */}
                {(() => {
                    const currentTab = tabs.find(t => t.id === activeTab);
                    const isGroup = currentTab?.type === 'group';
                    return (
                        <>
                            <div className={`flex items-center gap-1 border-b border-neutral-100 dark:border-neutral-800 overflow-x-auto scrollbar-hide scroll-smooth ${isGroup ? 'mb-2' : 'mb-6'}`}>
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`px-3 py-2 text-[10px] font-bold uppercase tracking-widest whitespace-nowrap transition-all relative ${activeTab === tab.id
                                            ? 'text-accent-primary'
                                            : 'text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300'
                                            }`}
                                    >
                                        {tab.label}
                                        {activeTab === tab.id && (
                                            <motion.div
                                                layoutId="product-tab-active"
                                                className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent-primary"
                                            />
                                        )}
                                    </button>
                                ))}
                            </div>

                            {/* Sub-tabs */}
                            {isGroup && (
                                <div className="flex items-center gap-1 mb-6 overflow-x-auto scrollbar-hide scroll-smooth py-1">
                                    {currentTab.children.map((sub: any) => (
                                        <button
                                            key={sub.id}
                                            onClick={() => setActiveSubTab(sub.id)}
                                            className={`px-2 py-1 text-[9px] font-bold uppercase tracking-wider rounded-md transition-all ${activeSubTab === sub.id
                                                ? 'bg-accent-primary/10 text-accent-primary'
                                                : 'text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300'
                                                }`}
                                        >
                                            {sub.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    );
                })()}

                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ 
                        opacity: { duration: 0.2 }
                    }}
                    className="space-y-8"
                >
                    {activeTabData.id === 'forecast' ? (
                        (() => {
                            const revenueChartData = computeModulatedTotalRevenue(
                                productForecastData.roas,
                                productForecastData.nonPaid,
                                { traffic: 100, conversion: 100, aov: 100, retention: 100, refundRate: 100 },
                                () => 50,
                                () => 100,
                                () => 50,
                                () => 100,
                                [],
                                [],
                                [],
                                { chart: null },
                                () => 50,
                                () => 100,
                                productForecastData.roas.perSourceCharts.reduce((acc: any, src: any) => ({ ...acc, [src.id]: src.avgSpend || 0 }), {})
                            );

                            const hasData = revenueChartData.length > 0 && revenueChartData.some((pt: any) => (pt.actual && pt.actual > 0) || (pt.forecast && pt.forecast > 0));
                            if (!hasData) {
                                return (
                                    <div>
                                        <div className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-3">{t('pages.forecast.product.revenueProjection', 'Revenue Projection')}</div>
                                        <ForecastEmptyState
                                            title={t('pages.forecast.product.noRevenueData', 'No Revenue Data Found')}
                                            description={t('pages.forecast.product.noRevenueDataDesc', "This product doesn't have enough tracked revenue yet to generate a forecast. Revenue will appear as orders are recorded.")}
                                        />
                                    </div>
                                );
                            }

                            return (
                        <div>
                            <div className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-3">{t('pages.forecast.product.revenueProjection', 'Revenue Projection')}</div>
                            <div className="h-[200px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={revenueChartData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default, #E5E7EB)" vertical={false} />
                                        <XAxis dataKey="label" hide />
                                        <YAxis tickFormatter={(v) => formatCompactNumber(v, currencySymbol)} tick={{ fontSize: 9, fill: '#6B7280' }} width={40} axisLine={false} tickLine={false} />
                                        <Tooltip
                                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                                            formatter={(val: number) => [formatCurrency(val, currencySymbol), 'Revenue']}
                                        />
                                        <Area type="monotone" dataKey="forecast" stroke="none" fill="#10b981" fillOpacity={0.1} isAnimationActive={false} />
                                        <Line type="monotone" dataKey="actual" stroke="#10b981" strokeWidth={2} dot={{ r: 0 }} isAnimationActive={false} />
                                        <Line type="monotone" dataKey="forecast" stroke="#10b981" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 0 }} isAnimationActive={false} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                            );
                        })()
                    ) : activeTabData.type === 'paid' && activeTabData.source ? (
                        (() => {
                            const srcData = activeTabData.source.data || [];
                            const hasActual = srcData.some((pt: any) => pt.actualRoas !== null && pt.actualRoas > 0);
                            const hasBaseline = srcData.some((pt: any) => pt.baselineRoas !== null && pt.baselineRoas > 0);
                            if (!hasActual && !hasBaseline) {
                                return (
                                    <ForecastEmptyState
                                        title={t('pages.forecast.product.noRevenueDataAvailable', 'No Revenue Data Available')}
                                        description={t('pages.forecast.product.noRevenueDataAvailableDesc', 'Not enough spend or revenue data linked to this ad source to compute a revenue forecast.')}
                                    />
                                );
                            }
                            // Apply modulators to populate forecastRoas field
                            const modulatedData = applyRoasModulators(
                                srcData,
                                50, // neutral optimism
                                100, // neutral noise
                                activeTabData.source.avgSpend,
                                activeTabData.source.avgSpend,
                                activeTabData.source.platform
                            );
                            
                            // Convert ROAS to Revenue
                            const revenueData = modulatedData.map((pt: any) => {
                                const spend = pt.actualSpend !== null ? pt.actualSpend : activeTabData.source.avgSpend;
                                return {
                                    ...pt,
                                    actualRevenue: pt.actualRevenue !== null ? pt.actualRevenue : null,
                                    forecastRevenue: pt.forecastRoas !== null ? pt.forecastRoas * spend : null,
                                    upperRevenue: pt.upperRoas !== null ? pt.upperRoas * spend : null,
                                    lowerRevenue: pt.lowerRoas !== null ? pt.lowerRoas * spend : null
                                };
                            });

                            return (
                        <div className="h-[200px] w-full">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                                    {t('pages.forecast.product.revenueForecastTitle', { source: activeTabData.source.source, defaultValue: `Revenue Forecast (${activeTabData.source.source})` })}
                                </span>
                                <div className="flex items-center gap-2">
                                    {activeTabData.source.phase && (
                                        <div className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${PHASE_METADATA[activeTabData.source.phase.current]?.color || ''}`}>
                                            {t(PHASE_METADATA[activeTabData.source.phase.current]?.labelKey) || activeTabData.source.phase.current}
                                        </div>
                                    )}
                                    {activeTabData.source.backtestScore !== null && (
                                        <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${activeTabData.source.backtestScore > 70 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                            {t('pages.forecast.accuracy.percentage', { score: activeTabData.source.backtestScore.toFixed(0), defaultValue: '{{score}}% Accuracy' })}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="h-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={revenueData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-default, #E5E7EB)" />
                                        <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                                        <YAxis tickFormatter={(v) => formatCompactNumber(v, currencySymbol)} tick={{ fontSize: 9, fill: '#6B7280' }} width={40} axisLine={false} tickLine={false} />
                                        <Tooltip formatter={(value: any, name: string) => [
                                            formatCurrency(Number(value), currencySymbol), 
                                            name === 'actualRevenue' ? t('pages.forecast.charts.actualRevenue', 'Actual Revenue') : 
                                            name === 'forecastRevenue' ? t('pages.forecast.charts.predictedRevenue', 'Predicted Revenue') : 
                                            name === 'upperRevenue' ? t('pages.forecast.charts.upperBound', 'Upper Bound') : 
                                            name === 'lowerRevenue' ? t('pages.forecast.charts.lowerBound', 'Lower Bound') : name
                                        ]} contentStyle={CHART_STYLES.tooltip.contentStyle} />
                                        <Area type="monotone" dataKey="upperRevenue" stroke="none" fill={activeTabData.source.color} fillOpacity={0.05} isAnimationActive={false} />
                                        <Area type="monotone" dataKey="lowerRevenue" stroke="none" fill="var(--bg-card, #ffffff)" isAnimationActive={false} />
                                        <Line type="monotone" dataKey="actualRevenue" stroke={activeTabData.source.color} strokeWidth={2} dot={{ r: 0 }} connectNulls={true} isAnimationActive={false} />
                                        <Line type="monotone" dataKey="forecastRevenue" stroke={activeTabData.source.color} strokeWidth={2} strokeDasharray="4 4" dot={{ r: 0 }} connectNulls={true} isAnimationActive={false} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                            );
                        })()
                    ) : activeTabData.type === 'organic' && activeTabData.source ? (
                        (() => {
                            const srcData = activeTabData.source.data || [];
                            const hasActual = srcData.some((pt: any) => pt.actual !== null && pt.actual > 0);
                            const hasBaseline = srcData.some((pt: any) => pt.baseline !== null && pt.baseline > 0);
                            if (!hasActual && !hasBaseline) {
                                return (
                                    <ForecastEmptyState
                                        title={t('pages.forecast.product.noOrganicData', 'No Organic Revenue Data')}
                                        description={t('pages.forecast.product.noOrganicDataDesc', "This product hasn't generated enough organic revenue yet for a reliable forecast.")}
                                    />
                                );
                            }
                            // Apply modulators to populate forecast field
                            const modulatedData = applyNonPaidModulators(
                                srcData,
                                50, // neutral optimism
                                100  // neutral noise
                            );
                            return (
                        <div className="h-[200px] w-full">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                                    {t('pages.forecast.product.organicRevenue', { source: activeTabData.source.source, defaultValue: `Organic Revenue (${activeTabData.source.source})` })}
                                </span>
                                {activeTabData.source.backtestScore !== null && (
                                    <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${activeTabData.source.backtestScore > 70 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                        {t('pages.forecast.product.accuracy', { percent: activeTabData.source.backtestScore.toFixed(0), defaultValue: `${activeTabData.source.backtestScore.toFixed(0)}% Accuracy` })}
                                    </span>
                                )}
                            </div>
                            <div className="h-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={modulatedData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-default, #E5E7EB)" />
                                        <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                                        <YAxis tickFormatter={(v) => formatCompactNumber(v, currencySymbol)} tick={{ fontSize: 9, fill: '#6B7280' }} width={40} axisLine={false} tickLine={false} />
                                        <Tooltip formatter={(value: any, name: string) => [formatCurrency(Number(value), currencySymbol), name]} contentStyle={CHART_STYLES.tooltip.contentStyle} />
                                        <Area type="monotone" dataKey="upper" stroke="none" fill={activeTabData.source.color} fillOpacity={0.05} isAnimationActive={false} />
                                        <Area type="monotone" dataKey="lower" stroke="none" fill="var(--bg-card, #ffffff)" isAnimationActive={false} />
                                        <Line type="monotone" dataKey="actual" stroke={activeTabData.source.color} strokeWidth={2} dot={{ r: 0 }} connectNulls={true} isAnimationActive={false} />
                                        <Line type="monotone" dataKey="forecast" stroke={activeTabData.source.color} strokeWidth={2} strokeDasharray="4 4" dot={{ r: 0 }} connectNulls={true} isAnimationActive={false} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                            );
                        })()
                    ) : null}
                </motion.div>
            </div>
        );
    };

    const RevenueSourceStats = ({ targetName, finance, entities, channels, roasForecastData, nonPaidForecastData, currencySymbol, isMobile, CHART_STYLES, formatCurrency, getOptimism, getVolatility, getNpOptimism, getNpVolatility, t, dateRange }: any) => {
        const [activeTab, setActiveTab] = useState<string | null>(null);

        const breakdown = useMemo(() => {
            if (!finance || !entities || !targetName) return null;
            const res = calculateRevenueSourceBreakdown(finance, entities, channels, dateRange, t);
            
            const isPaidTarget = targetName.endsWith(' - Paid');
            const cleanTargetName = isPaidTarget ? targetName.replace(' - Paid', '') : targetName;
            
            const matchesMap: Record<string, any> = {};
            let totalRevenue = 0;
            res.forEach(group => {
                group.links.forEach((link: any) => {
                    const matchName = link.source;
                    if (matchName === cleanTargetName && link.isPaid === isPaidTarget) {
                        totalRevenue += link.revenue;
                        link.matches.forEach((match: any) => {
                            const id = String(match.id);
                            if (!matchesMap[id]) {
                                matchesMap[id] = { ...match, revenue: 0 };
                            }
                            matchesMap[id].revenue += match.revenue;
                        });
                    }
                });
            });
            
            const matches = Object.values(matchesMap).sort((a: any, b: any) => b.revenue - a.revenue);
            if (matches.length === 0) return null;
            
            return {
                source: cleanTargetName,
                isPaid: isPaidTarget,
                revenue: totalRevenue,
                matches
            };
        }, [finance, entities, channels, dateRange, targetName, t]);

        const matchingSources = useMemo(() => {
            if (!breakdown) return { paid: [], organic: [] };
            
            const paid: any[] = [];
            const organic: any[] = [];
            
            breakdown.matches.forEach((match: any) => {
                if (breakdown.isPaid) {
                    const src = roasForecastData?.perSourceCharts?.find((s: any) => String(s.id) === String(match.id));
                    if (src) paid.push(src);
                } else {
                    const src = nonPaidForecastData?.charts?.find((s: any) => String(s.source) === breakdown.source);
                    if (src && !organic.find(o => o.source === src.source)) organic.push(src);
                }
            });
            
            return { paid, organic };
        }, [breakdown, roasForecastData, nonPaidForecastData]);

        const tabs = useMemo(() => {
            const paidTabs = matchingSources.paid.map((src: any) => ({
                id: `paid-${src.id}`,
                label: src.source,
                type: 'paid',
                source: src
            }));

            const organicTabs = matchingSources.organic.map((src: any) => ({
                id: `organic-${src.source}`,
                label: src.source,
                type: 'organic',
                source: src
            }));

            return [...paidTabs, ...organicTabs];
        }, [matchingSources]);

        React.useEffect(() => {
            if (tabs.length > 0 && (!activeTab || !tabs.some(t => t.id === activeTab))) {
                setActiveTab(tabs[0].id);
            }
        }, [tabs, activeTab]);

        const activeTabData = useMemo(() => {
            if (!activeTab) return null;
            return tabs.find(t => t.id === activeTab) || tabs[0];
        }, [tabs, activeTab]);

        if (tabs.length === 0 || !activeTabData) {
            return (
                <div className={`${isMobile ? 'p-0 pb-6' : 'p-6 bg-white dark:bg-neutral-950 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-xl'} overflow-hidden relative`}>
                    <ForecastEmptyState 
                        title={t('pages.forecast.product.noDetailedData', 'No detailed data')} 
                        description={t('pages.forecast.product.noDetailedDataDesc', 'Could not map any tracked campaigns to this revenue source.')} 
                    />
                </div>
            );
        }

        return (
            <div className={`${isMobile ? 'p-0 pb-6' : 'p-6 bg-white dark:bg-neutral-950 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-xl'} overflow-hidden relative`}>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 overflow-hidden flex-shrink-0 flex items-center justify-center">
                            <LineChart className="w-4 h-4 text-neutral-400" />
                        </div>
                        <h4 className="font-bold text-sm text-neutral-800 dark:text-neutral-200 truncate pr-4">
                            {targetName} Source
                        </h4>
                    </div>
                </div>

                {/* Tabs */}
                {tabs.length > 1 && (
                    <div className="flex items-center gap-1 border-b border-neutral-100 dark:border-neutral-800 overflow-x-auto scrollbar-hide scroll-smooth mb-6">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-3 py-2 text-[10px] font-bold uppercase tracking-widest whitespace-nowrap transition-all relative ${activeTab === tab.id
                                    ? 'text-accent-primary'
                                    : 'text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300'
                                    }`}
                            >
                                {tab.label}
                                {activeTab === tab.id && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent-primary"
                                    />
                                )}
                            </button>
                        ))}
                    </div>
                )}

                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ opacity: { duration: 0.2 } }}
                    className="space-y-8"
                >
                    {activeTabData.type === 'paid' && activeTabData.source ? (
                        (() => {
                            const srcData = activeTabData.source.data || [];
                            const modulatedData = applyRoasModulators(
                                srcData,
                                getOptimism(activeTabData.source.id), 
                                getVolatility(activeTabData.source.id), 
                                activeTabData.source.avgSpend,
                                activeTabData.source.avgSpend,
                                activeTabData.source.platform
                            );
                            
                            const revenueData = modulatedData.map((pt: any) => {
                                const spend = pt.actualSpend !== null ? pt.actualSpend : activeTabData.source.avgSpend;
                                return {
                                    ...pt,
                                    actualRevenue: pt.actualRevenue !== null ? pt.actualRevenue : null,
                                    forecastRevenue: pt.forecastRoas !== null ? pt.forecastRoas * spend : null,
                                    upperRevenue: pt.upperRoas !== null ? pt.upperRoas * spend : null,
                                    lowerRevenue: pt.lowerRoas !== null ? pt.lowerRoas * spend : null
                                };
                            });

                            return (
                                <div className="h-[200px] w-full">
                                    <div className="flex items-center justify-between mb-4">
                                        <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">{t('pages.forecast.product.revenueForecastTitle', { source: activeTabData.source.source })}</span>
                                        <div className="flex items-center gap-2">
                                            {activeTabData.source.phase && (
                                                <div className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${PHASE_METADATA[activeTabData.source.phase.current]?.color || ''}`}>
                                                    {t(PHASE_METADATA[activeTabData.source.phase.current]?.labelKey) || activeTabData.source.phase.current}
                                                </div>
                                            )}
                                            {activeTabData.source.backtestScore !== null && (
                                                <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${activeTabData.source.backtestScore > 70 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                                    {t('pages.forecast.product.accuracy', { percent: activeTabData.source.backtestScore.toFixed(0) })}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="h-full">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <ComposedChart data={revenueData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-default, #E5E7EB)" />
                                                <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                                                <YAxis tickFormatter={(v) => formatCompactNumber(v, currencySymbol)} tick={{ fontSize: 9, fill: '#6B7280' }} width={40} axisLine={false} tickLine={false} />
                                                <Tooltip formatter={(value: any, name: string) => [formatCurrency(Number(value), currencySymbol), name === 'actualRevenue' ? t('pages.forecast.charts.actualRevenue') : name === 'forecastRevenue' ? t('pages.forecast.charts.predictedRevenue') : name === 'upperRevenue' ? t('pages.forecast.charts.upperBound') : name === 'lowerRevenue' ? t('pages.forecast.charts.lowerBound') : name]} contentStyle={CHART_STYLES.tooltip.contentStyle} />
                                                <Area type="monotone" dataKey="upperRevenue" stroke="none" fill={activeTabData.source.color} fillOpacity={0.05} />
                                                <Line type="monotone" dataKey="actualRevenue" stroke={activeTabData.source.color} strokeWidth={2} dot={{ r: 0 }} connectNulls={true} />
                                                <Line type="monotone" dataKey="forecastRevenue" stroke={activeTabData.source.color} strokeWidth={2} strokeDasharray="4 4" dot={{ r: 0 }} connectNulls={true} />
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            );
                        })()
                    ) : activeTabData.type === 'organic' && activeTabData.source ? (
                        (() => {
                            const srcData = activeTabData.source.data || [];
                            const modulatedData = applyNonPaidModulators(
                                srcData,
                                getNpOptimism(`np-${activeTabData.source.source}`), 
                                getNpVolatility(`np-${activeTabData.source.source}`)
                            );
                            return (
                                <div className="h-[200px] w-full">
                                    <div className="flex items-center justify-between mb-4">
                                        <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">{t('pages.forecast.product.organicRevenue', { source: activeTabData.source.source })}</span>
                                        {activeTabData.source.backtestScore !== null && (
                                            <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${activeTabData.source.backtestScore > 70 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                                {t('pages.forecast.product.accuracy', { percent: activeTabData.source.backtestScore.toFixed(0) })}
                                            </span>
                                        )}
                                    </div>
                                    <div className="h-full">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <ComposedChart data={modulatedData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-default, #E5E7EB)" />
                                                <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                                                <YAxis tickFormatter={(v) => formatCompactNumber(v, currencySymbol)} tick={{ fontSize: 9, fill: '#6B7280' }} width={40} axisLine={false} tickLine={false} />
                                                <Tooltip formatter={(value: any, name: string) => [formatCurrency(Number(value), currencySymbol), name === 'actual' ? t('pages.forecast.charts.actualRevenue') : name === 'forecast' ? t('pages.forecast.charts.predictedRevenue') : name]} contentStyle={CHART_STYLES.tooltip.contentStyle} />
                                                <Area type="monotone" dataKey="upper" stroke="none" fill={activeTabData.source.color} fillOpacity={0.05} />
                                                <Line type="monotone" dataKey="actual" stroke={activeTabData.source.color} strokeWidth={2} dot={{ r: 0 }} connectNulls={true} />
                                                <Line type="monotone" dataKey="forecast" stroke={activeTabData.source.color} strokeWidth={2} strokeDasharray="4 4" dot={{ r: 0 }} connectNulls={true} />
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            );
                        })()
                    ) : null}
                </motion.div>
            </div>
        );
    };
    // DERIVED TOTALS (from Bottom-Up Forecast)
    // Summary statistics computed from the combined revenue chart.
    // ============================================================
    const {
        historicalTotal, forecastedTotal,
        dataPointsCount
    } = useMemo(() => {
        if (!modulatedTotalRevenueChart || modulatedTotalRevenueChart.length === 0) {
            return { historicalTotal: 0, forecastedTotal: 0, dataPointsCount: 0 };
        }
        const hTotal = modulatedTotalRevenueChart.filter((pt: any) => !pt.isForecast).reduce((sum: number, pt: any) => sum + (pt.actual || 0), 0);
        const fTotal = modulatedTotalRevenueChart.filter((pt: any) => pt.isForecast).reduce((sum: number, pt: any) => sum + (pt.forecast || 0), 0);
        const dpCount = modulatedTotalRevenueChart.filter((pt: any) => !pt.isForecast).length;

        return { historicalTotal: hTotal, forecastedTotal: fTotal, dataPointsCount: dpCount };
    }, [modulatedTotalRevenueChart]);

    // ============================================================
    // STATISTICAL ACCURACY (comprehensive 5-factor model)
    //
    // Accounts for: revenue-weighted backtests, data quality,
    // slider fidelity, forecast horizon, and source coverage.
    //
    // Dependencies: roasForecastData, nonPaidForecastData, forecastDays, all sliders
    // ============================================================
    const { score: accuracyScore, breakdown: accuracyBreakdown } = useMemo(
        () => computeStatisticalAccuracy(
            roasForecastData,
            nonPaidForecastData,
            forecastDays,
            getOptimism,
            getVolatility,
            getNpOptimism,
            getNpVolatility,
            journals,
            journalOperations,
            specs,
            unknownForecastData,
            getUnkOptimism,
            getUnkVolatility
        ),
        [roasForecastData, nonPaidForecastData, forecastDays, unknownForecastData, journals, journalOperations, specs]
    );

    // ============================================================
    // STAGE 3e: PER-INPUT CHARTS (Journal & Spec Charts)
    // ============================================================
    const journalCharts: JournalChart[] = useMemo(
        () => computeJournalCharts(journals, journalOperations, roasForecastData, finance),
        [journals, journalOperations, roasForecastData, finance]
    );

    const specCharts: SpecChart[] = useMemo(
        () => computeSpecCharts(specs, roasForecastData, nonPaidForecastData, journals, journalOperations, unknownForecastData),
        [specs, roasForecastData, nonPaidForecastData, journals, journalOperations, unknownForecastData]
    );



    // ============================================================
    // UI COMPONENTS
    // ============================================================

    // OLD NESTED COMPONENTS REMOVED


    // OLD NESTED COMPONENTS REMOVED


    // OLD NESTED COMPONENTS REMOVED

    // OLD NESTED COMPONENTS REMOVED

    // OLD NESTED COMPONENTS REMOVED

    const renderProductMetric = React.useCallback((p: BoardEntity) => {
        const val = productForecastsMap[p.id];
        if (val === undefined || val === null) return null;
        
        if (val <= 0) {
            return (
                <div className="flex items-center gap-1 bg-neutral-900/60 backdrop-blur-sm px-1.5 py-0.5 rounded text-[8px] font-bold text-neutral-300 border border-white/10 uppercase tracking-tighter shadow-sm">
                    <Info className="w-2.5 h-2.5 opacity-70" />
                    <span>{t('pages.forecast.product.limitedData', 'Limited Data')}</span>
                </div>
            );
        }

        return (
            <div className="flex flex-col items-center gap-0 bg-neutral-900/60 backdrop-blur-sm px-2 py-0.5 rounded text-[8px] font-black text-white border border-white/20 shadow-lg">
                <div className="flex items-center gap-1 opacity-80 uppercase tracking-tighter">
                    <TrendingUp className="w-2.5 h-2.5 text-emerald-400" />
                    <span>{t('pages.forecast.product.forecast', 'Forecast')}</span>
                </div>
                <span className="text-emerald-400 leading-none mt-0.5">
                    {formatCurrency(val, currencySymbol)}
                </span>
            </div>
        );
    }, [productForecastsMap, formatCurrency, currencySymbol]);

    const isEndDateYesterday = useMemo(() => {
        if (!dateRange?.endDate) return false;
        return isYesterday(new Date(dateRange.endDate));
    }, [dateRange]);

    const hasNoDataForForecast = useMemo(() => {
        const hasRoas = roasForecastData?.perSourceCharts?.length > 0;
        const hasNonPaid = nonPaidForecastData?.charts?.length > 0;
        const hasUnknown = !!unknownForecastData?.chart;
        return !hasRoas && !hasNonPaid && !hasUnknown;
    }, [roasForecastData, nonPaidForecastData, unknownForecastData]);

    if (!isEndDateYesterday) {
        return (
            <div className="h-full flex flex-col bg-white dark:bg-neutral-900 overflow-hidden">
                <ForecastPageEmptyState 
                    t={t} 
                    title={t('pages.forecast.empty.notYesterdayTitle', 'Adjust Your View')}
                    description={t('pages.forecast.empty.notYesterdayDescription', 'Forecasting is most accurate when based on finalized data up to yesterday. Please adjust your range to end on the previous day.')}
                    hideRefresh
                    onAction={() => window.dispatchEvent(new CustomEvent('dashbal:open-datepicker'))}
                    actionLabel={t('actions.adjustDateRange', 'Adjust Date Range')}
                />
            </div>
        );
    }

    if (hasNoDataForForecast) {
        return (
            <div className="h-full flex flex-col bg-white dark:bg-neutral-900 overflow-hidden">
                <ForecastPageEmptyState 
                    t={t} 
                    title={t('pages.forecast.empty.noDataTitle')}
                    description={t('pages.forecast.empty.noDataDescription')}
                />
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col bg-white dark:bg-neutral-900 overflow-hidden relative">
            <div className="flex-1 relative overflow-hidden">
                <div 
                    className="h-full overflow-y-auto fade-scrollbar scroll-smooth px-2 lg:px-4 pb-24 lg:pb-24"
                    onScroll={(e) => {
                        const container = e.currentTarget;
                        container.classList.add('scrolling');
                        const existingTimeout = (container as any).dataset.scrollTimeout;
                        if (existingTimeout) clearTimeout(parseInt(existingTimeout));
                        const timeoutId = setTimeout(() => {
                            container.classList.remove('scrolling');
                        }, 1000);
                        (container as any).dataset.scrollTimeout = timeoutId.toString();
                    }}
                >
                    <div className="pt-2 lg:pt-4 space-y-4 lg:space-y-6 w-full">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-4 w-full items-start">
                            {/* Main Content Area */}
                            <div className="lg:col-span-8 flex flex-col gap-2 lg:gap-4">

                                {/* Moved KPIs to Top (Mobile Only) */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-start w-full lg:hidden">
                                    <ForecastKpiCards
                                        activeKpiView={activeKpiView}
                                        setActiveKpiView={(val: any) => {
                                            setActiveKpiView(val);
                                            if (val) setSelectedRevenueSource(null);
                                        }}
                                        forecastedTotal={forecastedTotal}
                                        currencySymbol={currencySymbol}
                                        accuracyScore={accuracyScore}
                                        formatCurrency={formatCurrency}
                                        selectedProduct={selectedProduct}
                                        setSelectedProduct={setSelectedProduct}
                                        t={t}
                                    />
                                </div>

                                {/* Divider for Mobile */}
                                <div className="lg:hidden flex items-center justify-center py-2 mt-2 mb-1">
                                    <div className="w-1/4 h-px bg-gradient-to-r from-transparent to-neutral-200 dark:to-neutral-800" />
                                    <div className="px-3 text-[9px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">
                                        Forecasted Sources
                                    </div>
                                    <div className="w-1/4 h-px bg-gradient-to-l from-transparent to-neutral-200 dark:to-neutral-800" />
                                </div>

                                <RevenueSourceCards
                                    targets={aggregatedBreakdownTargets}
                                    currencySymbol={currencySymbol}
                                    formatCurrency={formatCurrency}
                                    t={t}
                                    activeRevenueSource={selectedRevenueSource}
                                    onTargetSelect={(targetName: string | null) => {
                                        setSelectedRevenueSource(targetName);
                                        if (targetName) {
                                            setActiveKpiView(null);
                                            setSelectedProduct(null);
                                        }
                                    }}
                                />

                                {/* Product Explorer - Positioned below the main layout */}
                                <ProductExplorer
                                    channels={channels}
                                    entities={entities}
                                    onProductSelect={(p: BoardEntity | null) => {
                                        setSelectedProduct(p);
                                        if (p) {
                                            setActiveKpiView(null);
                                            setSelectedRevenueSource(null);
                                        }
                                    }}
                                    selectedProductId={selectedProductId}
                                    renderProductMetric={renderProductMetric}
                                />

                            </div>

                            {/* Sidebar */}
                            <div className="lg:col-span-4 hidden lg:flex flex-col gap-6">
                                <div className="grid grid-cols-1 gap-2 items-start w-full">
                                    <ForecastKpiCards
                                        activeKpiView={activeKpiView}
                                        setActiveKpiView={(val: any) => {
                                            setActiveKpiView(val);
                                            if (val) setSelectedRevenueSource(null);
                                        }}
                                        forecastedTotal={forecastedTotal}
                                        currencySymbol={currencySymbol}
                                        accuracyScore={accuracyScore}
                                        formatCurrency={formatCurrency}
                                        selectedProduct={selectedProduct}
                                        setSelectedProduct={setSelectedProduct}
                                        t={t}
                                    />
                                </div>

                                <AIVerdictModule
                                    pageId="forecast"
                                    boardId={boardId || ''}
                                />



                            </div>
                        </div>

                        {/* ALL CHARTS - SHOWS ONLY IF DEBUG ENABLED OR EXPLICITLY ON */}
                        <AnimatePresence>
                            {showDebugCharts && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                    className="flex flex-col space-y-6 mt-6"
                                >
                                    {/* Per-Source ROAS Building Blocks */}
                                    {roasForecastData.perSourceCharts.length > 0 && (
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2 px-1">
                                                <Target className="w-4 h-4 text-neutral-500" />
                                                <h3 className="text-xs font-bold text-neutral-500 uppercase tracking-widest">{t('pages.forecast.charts.perSourceRoas', 'Per-Source ROAS Forecast')}</h3>
                                            </div>
                                            <div className={`grid gap-4 ${roasForecastData.perSourceCharts.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                                                {roasForecastData.perSourceCharts.map((src, idx) => (
                                                    <RoasChartBlock
                                                        key={src.id}
                                                        data={src.data}
                                                        title={`${src.source} ROAS`}
                                                        stepNum={idx + 1}
                                                        color={src.color}
                                                        sourceKey={src.id}
                                                        halfLife={src.halfLife}
                                                        halfLifeSource={src.halfLifeSource}
                                                        backtestScore={src.backtestScore}
                                                        phase={src.phase}
                                                        roasForecastData={roasForecastData}
                                                        optValue={getOptimism(src.id)}
                                                        noiseValue={getVolatility(src.id)}
                                                    />
                                                ))}
                                                {unknownForecastData?.chart && (
                                                    <NonPaidChartBlock
                                                        data={unknownForecastData.chart.data}
                                                        title={t('pages.forecast.charts.unknownSourceRevenue', 'Unknown Source Revenue')}
                                                        stepNum={roasForecastData.perSourceCharts.length + 1}
                                                        color={CHART_COLORS.gray[400]}
                                                        currencySymbol={currencySymbol}
                                                        optValue={getUnkOptimism("unknown")}
                                                        noiseValue={getUnkVolatility("unknown")}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Final Combined ROAS */}
                                    <RoasChartBlock
                                        data={modulatedCombinedChart}
                                        title={t('pages.forecast.charts.combinedRoasTrajectory', 'Combined ROAS Trajectory')}
                                        stepNum={roasForecastData.perSourceCharts.length + 1}
                                        color="#8b5cf6"
                                        sourceKey="combined"
                                        roasForecastData={roasForecastData}
                                        hideSliders={true}
                                    />

                                    {/* Final Combined Total Revenue */}
                                    <CombinedRevenueChart
                                        data={modulatedTotalRevenueChart}
                                        title={t('pages.forecast.charts.combinedRevenue', 'Combined Total Revenue')}
                                        stepNum={roasForecastData.perSourceCharts.length + 2}
                                        currencySymbol={currencySymbol}
                                    />

                                    {/* Non-Paid Revenue Forecasts Section */}
                                    <NonPaidForecastSection
                                        nonPaidForecastData={nonPaidForecastData}
                                        currencySymbol={currencySymbol}
                                        getNpOptimism={getNpOptimism}
                                        getNpVolatility={getNpVolatility}
                                    />

                                    {/* Combined Non-Paid Revenue */}
                                    {modulatedCombinedNonPaidChart.length > 0 && (
                                        <div className="bg-white dark:bg-neutral-900 rounded-xl p-5 border border-emerald-100 dark:border-emerald-900/30 shadow-sm flex flex-col min-h-[320px]">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black text-white bg-emerald-600">Σ</div>
                                                    <h3 className="font-bold text-sm text-neutral-800 dark:text-neutral-200">{t('pages.forecast.charts.combinedNonPaidRevenue', 'Combined Non-Paid Revenue')}</h3>
                                                </div>
                                            </div>
                                            <div className="flex-1 w-full min-h-[220px]">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <ComposedChart data={modulatedCombinedNonPaidChart} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                                                        <defs>
                                                            <linearGradient id="npCombinedConf" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.05} />
                                                                <stop offset="95%" stopColor="#10b981" stopOpacity={0.005} />
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-default, #E5E7EB)" />
                                                        <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6B7280' }} axisLine={false} tickLine={false} />
                                                        <YAxis tickFormatter={(v) => formatCompactNumber(v, currencySymbol)} tick={{ fontSize: 9, fill: '#6B7280' }} width={50} axisLine={false} tickLine={false} />
                                                        <Tooltip formatter={(value: any, name: string) => [
                                                            formatCurrency(Number(value), currencySymbol), 
                                                            name === 'actual' ? t('pages.forecast.charts.actualRevenue', 'Actual Revenue') : 
                                                            name === 'forecast' ? t('pages.forecast.charts.predictedRevenue', 'Predicted Revenue') : 
                                                            name === 'upper' ? t('pages.forecast.charts.upperBound', 'Upper Bound') : 
                                                            name === 'lower' ? t('pages.forecast.charts.lowerBound', 'Lower Bound') : name
                                                        ]} contentStyle={CHART_STYLES.tooltip.contentStyle} />
                                                        <Area type="monotone" dataKey="upper" stroke="none" fill="url(#npCombinedConf)" />
                                                        <Area type="monotone" dataKey="lower" stroke="none" fill="var(--bg-card, #ffffff)" />
                                                        <Line type="monotone" dataKey="actual" stroke="#10b981" strokeWidth={2.5} dot={{ r: 0 }} connectNulls={true} />
                                                        <Line type="monotone" dataKey="forecast" stroke="#10b981" strokeWidth={2.5} strokeDasharray="5 5" dot={{ r: 0 }} connectNulls={true} />
                                                    </ComposedChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    )}

                                    {/* Manual Journals Section */}
                                    {journalCharts.length > 0 && (
                                        <div className="col-span-1 md:col-span-2 mt-8">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 mt-4">
                                                {journalCharts.map((jc, i) => (
                                                    <GenericForecastChartBlock
                                                        key={jc.journalId}
                                                        data={jc.data}
                                                        title={jc.journalName}
                                                        stepNum={`J${i + 1}`}
                                                        color={CHART_COLORS.expense}
                                                        subtitle={t('pages.forecast.charts.manualEntries', 'Manual Entries') as string}
                                                        currencySymbol={currencySymbol}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Board Specs Section */}
                                    {specCharts.length > 0 && (
                                        <div className="col-span-1 md:col-span-2 mt-8">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 mt-4">
                                                {specCharts.map((sc, i) => (
                                                    <GenericForecastChartBlock
                                                        key={sc.specId}
                                                        data={sc.data}
                                                        title={sc.specName}
                                                        stepNum={`S${i + 1}`}
                                                        color={CHART_COLORS.primary}
                                                        subtitle={t('pages.forecast.charts.boardSpecs', 'Board Specs') as string}
                                                        currencySymbol={currencySymbol}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Expenses Forecast */}
                                    {expenseForecastData.combinedChart.length > 0 && (
                                        <>
                                            <ExpenseForecastChart
                                                data={expenseForecastData.combinedChart}
                                                title={t('pages.forecast.charts.expensesForecast', 'Expenses Forecast')}
                                                stepNum={roasForecastData.perSourceCharts.length + 3}
                                                currencySymbol={currencySymbol}
                                            />
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 mt-2">
                                                {expenseForecastData.sources.map((src, i) => (
                                                    <GenericForecastChartBlock
                                                        key={src.sourceId}
                                                        data={src.data}
                                                        title={src.sourceName}
                                                        stepNum={`E${i + 1}`}
                                                        color={src.color}
                                                        subtitle={src.type}
                                                        currencySymbol={currencySymbol}
                                                    />
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Bottom Drawer - Replaces desktop sidebar content when product or KPI is selected */}
                        <BoardBottomDrawer
                            isOpen={!!(isAiMobileExpanded || selectedProductId || activeKpiView || selectedRevenueSource)}
                            className={(!selectedProductId && !activeKpiView && !selectedRevenueSource) ? 'lg:hidden' : ''}
                            onClose={() => {
                                setSelectedProduct(null);
                                setSelectedRevenueSource(null);
                                setActiveKpiView(null);
                                setIsAiMobileExpanded(false);
                            }}
                            onOpen={() => {
                                setIsAiMobileExpanded(true);
                            }}
                            height="100%"
                            initialHeight="84px"
                            showTints={!selectedProductId && !activeKpiView && !selectedRevenueSource}
                        >
                            {selectedProductId ? (
                                <ProductStats
                                    selectedProductId={selectedProductId}
                                    productForecastData={productForecastData}
                                    entities={entities}
                                    currencySymbol={currencySymbol}
                                    isMobile={true}
                                    computeModulatedTotalRevenue={computeModulatedTotalRevenue}
                                    CHART_STYLES={CHART_STYLES}
                                    formatCurrency={formatCurrency}
                                    t={t}
                                />
                            ) : selectedRevenueSource ? (
                                <RevenueSourceStats
                                    targetName={selectedRevenueSource}
                                    finance={finance}
                                    entities={entities}
                                    channels={channels}
                                    roasForecastData={roasForecastData}
                                    nonPaidForecastData={nonPaidForecastData}
                                    currencySymbol={currencySymbol}
                                    isMobile={true}
                                    CHART_STYLES={CHART_STYLES}
                                    formatCurrency={formatCurrency}
                                    getOptimism={getOptimism}
                                    getVolatility={getVolatility}
                                    getNpOptimism={getNpOptimism}
                                    getNpVolatility={getNpVolatility}
                                    t={t}
                                    dateRange={dateRange}
                                />
                            ) : activeKpiView === 'revenue' ? (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="p-4 flex-1 overflow-y-auto scrollbar-hide"
                                >
                                    <div className="flex items-center gap-2 mb-6">
                                        <div className="p-1.5 rounded-lg bg-success-500 bg-opacity-10">
                                            <TrendingUp className="w-4 h-4 text-success-600" />
                                        </div>
                                        <h4 className="font-bold text-lg text-neutral-800 dark:text-neutral-200">{t('pages.forecast.kpis.forecastedRevenue', 'Forecasted Revenue')}</h4>
                                    </div>
                                    <div className="text-4xl font-black text-neutral-900 dark:text-neutral-100 mb-2 truncate">
                                        {formatCurrency(forecastedTotal, currencySymbol)}
                                    </div>
                                    <div className="flex items-center gap-2 text-sm font-semibold text-success-600 dark:text-success-400 mb-6">
                                        <span className="bg-success-500/10 px-2 py-0.5 rounded text-xs">+{forecastDays} Days</span>
                                        <span className="text-neutral-400 text-xs font-medium">{t('pages.forecast.kpis.expectedTotal', 'Expected Total')}</span>
                                    </div>



                                    <div className="mt-8 mb-8 w-full h-[250px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <ComposedChart data={revenueForecastChart} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                                                <defs>
                                                    <linearGradient id="revForecastConfDrawer" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.05} />
                                                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0.005} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                                <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                                                <YAxis tickFormatter={(v) => formatCompactNumber(v, currencySymbol)} tick={{ fontSize: 9, fill: '#94a3b8' }} width={40} axisLine={false} tickLine={false} />
                                                <Tooltip formatter={(value: any, name: string) => {
                                                    if (value === null) return ['-', name];
                                                    return [formatCurrency(Number(value), currencySymbol),
                                                    name === 'actual' ? t('common.actual') : name === 'forecast' ? t('common.forecast') : name === 'upper' ? t('common.upper') : name === 'lower' ? t('common.lower') : name];
                                                }} contentStyle={{ ...CHART_STYLES.tooltip.contentStyle, fontSize: '10px' }} />
                                                <Area type="monotone" dataKey="upper" stroke="none" fill="url(#revForecastConfDrawer)" isAnimationActive={false} />
                                                <Area type="monotone" dataKey="lower" stroke="none" fill="white" isAnimationActive={false} />
                                                <Line type="monotone" dataKey="actual" stroke="#6366f1" strokeWidth={2} dot={{ r: 0 }} connectNulls={true} isAnimationActive={false} />
                                                <Line type="monotone" dataKey="forecast" stroke="#6366f1" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 0 }} connectNulls={true} isAnimationActive={false} />
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </div>

                                    <ForecastDistributionBar 
                                        segments={aggregatedBreakdownTargets} 
                                        formatCurrency={formatCurrency} 
                                        currencySymbol={currencySymbol} 
                                    />

                                    <AIVerdictModule
                                        pageId="forecast"
                                        boardId={boardId || ''}
                                    />
                                </motion.div>
                            ) : activeKpiView === 'accuracy' ? (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="p-4"
                                >
                                    <div className="flex items-center gap-2 mb-6">
                                        <div className="p-1.5 rounded-lg bg-accent-primary bg-opacity-10">
                                            <Activity className="w-4 h-4 text-accent-primary" />
                                        </div>
                                        <h4 className="font-bold text-lg text-neutral-800 dark:text-neutral-200">{t('pages.forecast.accuracy.title', 'Statistical Accuracy')}</h4>
                                    </div>
                                    <div className="flex items-end gap-3 mb-2">
                                        <div className="text-4xl font-black text-neutral-900 dark:text-neutral-100">{accuracyScore.toFixed(1)}%</div>
                                        <div className="text-xs text-neutral-400 font-medium mb-1.5">{t('pages.forecast.accuracy.reliabilityLabel', 'Forecast Reliability')}</div>
                                    </div>
                                    <div className="text-[10px] text-neutral-400 mb-3">
                                        {accuracyScore > 65 ? t('pages.forecast.accuracy.verdicts.good', 'Good — Enough stable data to produce a meaningful forecast.') :
                                         accuracyScore > 40 ? t('pages.forecast.accuracy.verdicts.moderate', 'Moderate — Some data gaps. Treat as a rough directional estimate.') :
                                         t('pages.forecast.accuracy.verdicts.low', 'Low — Very limited data. This forecast is a best-effort guess, not a reliable prediction.')}
                                    </div>
                                    <div className="w-full bg-neutral-100 dark:bg-neutral-800 rounded-full h-2 mb-8">
                                        <div className={`h-2 rounded-full ${accuracyScore > 65 ? 'bg-success-500' : accuracyScore > 40 ? 'bg-amber-500' : 'bg-rose-500'}`}
                                            style={{ width: `${Math.min(100, accuracyScore)}%` }} />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {Object.entries({
                                            [t('pages.forecast.accuracy.backtest.label', 'Backtest')]: { val: accuracyBreakdown.weightedBacktest, key: "backtest" },
                                            [t('pages.forecast.accuracy.quality.label', 'Data Quality')]: { val: accuracyBreakdown.qualityFactor, key: "quality" },
                                            [t('pages.forecast.accuracy.fidelity.label', 'Slider Fidelity')]: { val: accuracyBreakdown.sliderFidelity, key: "fidelity" },
                                            [t('pages.forecast.accuracy.horizon.label', 'Horizon')]: { val: accuracyBreakdown.horizonDecay, key: "horizon" },
                                            [t('pages.forecast.accuracy.coverage.label', 'Coverage')]: { val: accuracyBreakdown.coverageFactor, key: "coverage" }
                                        }).map(([label, { val, key }]) => (
                                            <div key={label} className="bg-neutral-50 dark:bg-neutral-800/50 p-3 rounded-xl border border-neutral-100 dark:border-neutral-800">
                                                <div className="flex justify-between items-center mb-1">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">{label}</span>
                                                        <button onClick={(e) => { e.stopPropagation(); setInfoModal({ title: t(ACCURACY_INFO[key].titleKey), body: t(ACCURACY_INFO[key].bodyKey) }); }} className="p-0.5 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded transition-colors">
                                                            <Info className="w-2.5 h-2.5 text-neutral-400" />
                                                        </button>
                                                    </div>
                                                    <span className="text-xs font-black text-neutral-800 dark:text-neutral-200">{val.toFixed(0)}%</span>
                                                </div>
                                                <div className="w-full bg-neutral-200 dark:bg-neutral-800/80 h-1 rounded-full">
                                                    <div className="h-1 bg-accent-primary rounded-full" style={{ width: `${val}%` }} />
                                                </div>
                                            </div>
                                        ))}
                                        <div className="bg-neutral-50 dark:bg-neutral-800/50 p-3 rounded-xl border border-neutral-100 dark:border-neutral-800">
                                            <div className="flex justify-between items-center mb-1">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">{t('pages.forecast.accuracy.sources.title', 'Sources Tested')}</span>
                                                    <button onClick={(e) => { e.stopPropagation(); setInfoModal({ title: t(ACCURACY_INFO.sources.titleKey), body: t(ACCURACY_INFO.sources.bodyKey) }); }} className="p-0.5 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded transition-colors">
                                                        <Info className="w-2.5 h-2.5 text-neutral-400" />
                                                    </button>
                                                </div>
                                                <span className="text-xs font-black text-neutral-800 dark:text-neutral-200">{accuracyBreakdown.backtestableCount}/{accuracyBreakdown.sourceCount}</span>
                                            </div>
                                            <div className="w-full bg-neutral-200 dark:bg-neutral-800/80 h-1 rounded-full">
                                                <div className="h-1 bg-accent-primary rounded-full" style={{ width: `${(accuracyBreakdown.backtestableCount / accuracyBreakdown.sourceCount) * 100}%` }} />
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ) : (
                                <div className="cursor-pointer" onClick={() => !isAiMobileExpanded && setIsAiMobileExpanded(true)}>
                                    <div className="text-[10px] font-bold text-accent-primary uppercase tracking-widest mb-3 flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-accent-primary animate-pulse" />
                                            AI Prediction Verdict
                                        </div>
                                    </div>
                                    <h4 className={`font-bold text-text-primary mb-2 transition-all ${isAiMobileExpanded ? 'text-lg' : 'text-sm'}`}>
                                        {forecastedTotal > historicalTotal ? t('pages.forecast.verdict.growth', 'Growth Expected') : t('pages.forecast.verdict.steady', 'Steady Trajectory')}
                                    </h4>

                                    {isAiMobileExpanded && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ duration: 0.2 }}
                                            className="pt-2"
                                        >
                                            <AIVerdictModule
                                                pageId="forecast"
                                                boardId={boardId || ''}
                                            />
                                        </motion.div>
                                    )}
                                </div>
                            )}
                        </BoardBottomDrawer>
            </div>

            {/* Sticky Footer for Controls */}
            <div className="px-4 py-2 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 flex items-center justify-between shrink-0 z-20">
                <div className="flex items-center gap-4">
                    {modulatedTotalRevenueChart.length > 0 && (
                        <div className="hidden md:flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                                <span className="text-[9px] font-black text-neutral-400 uppercase tracking-widest">{t('pages.forecast.controls.historical', 'Historical:')}</span>
                                <span className="text-[10px] font-bold text-neutral-600 dark:text-neutral-300">
                                    {formatDate(new Date(modulatedTotalRevenueChart[0].date), 'MMM d')} - {formatDate(new Date(modulatedTotalRevenueChart.find(p => !p.isForecast && modulatedTotalRevenueChart[modulatedTotalRevenueChart.indexOf(p) + 1]?.isForecast)?.date || modulatedTotalRevenueChart[dataPointsCount - 1].date), 'MMM d, yyyy')}
                                </span>
                            </div>
                            <div className="w-px h-3 bg-neutral-200 dark:bg-neutral-800" />
                            <div className="flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-accent-primary/40 animate-pulse" />
                                <span className="text-[9px] font-black text-neutral-400 uppercase tracking-widest">{t('pages.forecast.controls.forecast', 'Forecast:')}</span>
                                <span className="text-[10px] font-bold text-accent-primary">
                                    {formatDate(new Date(modulatedTotalRevenueChart.find(p => p.isForecast)?.date || new Date()), 'MMM d')} - {formatDate(new Date(modulatedTotalRevenueChart[modulatedTotalRevenueChart.length - 1].date), 'MMM d, yyyy')}
                                </span>
                            </div>
                        </div>
                    )}
                    {/* Mobile optimized dates */}
                    {modulatedTotalRevenueChart.length > 0 && (
                        <div className="flex md:hidden flex-col gap-0">
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-bold text-neutral-800 dark:text-neutral-200 tracking-tight">
                                    {formatDate(new Date(modulatedTotalRevenueChart[0].date), 'MMM d')} - {formatDate(new Date(modulatedTotalRevenueChart[modulatedTotalRevenueChart.length - 1].date), 'MMM d, yyyy')}
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="w-1 h-1 rounded-full bg-accent-primary animate-pulse" />
                                <span className="text-[8px] font-black text-neutral-400 uppercase tracking-widest">
                                    {t('pages.forecast.controls.window', 'Forecast Window')}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    {isDebugEnabled && (
                        <div className="flex items-center gap-2 mr-1">
                            <span className="text-[10px] font-black text-neutral-400 uppercase tracking-widest hidden sm:inline">{t('pages.forecast.controls.debug', 'Debug Charts')}</span>
                            <button
                                onClick={() => setShowDebugCharts(!showDebugCharts)}
                                className={`relative w-8 h-4 rounded-full transition-all duration-200 focus:outline-none ${showDebugCharts ? 'bg-accent-primary' : 'bg-neutral-300 dark:bg-neutral-700'}`}
                            >
                                <motion.div
                                    animate={{ x: showDebugCharts ? 18 : 2 }}
                                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                    className="absolute top-1 w-2 h-2 bg-white rounded-full shadow-sm"
                                />
                            </button>
                        </div>
                    )}
                    <div className="flex bg-neutral-100 dark:bg-neutral-800 p-0.5 rounded-lg border border-neutral-200 dark:border-neutral-700 shrink-0">
                        {[7, 30, 90].map((days) => (
                            <button key={days}
                                className={`text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md transition-all ${forecastDays === days ? 'bg-white dark:bg-neutral-700 shadow-sm text-neutral-800 dark:text-neutral-200' : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'}`}
                                onClick={() => setForecastDays(days as 7 | 30 | 90)}>
                                {days}d
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Modals */}
            <AnimatePresence>
                {infoModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setInfoModal(null)}>
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-800 shadow-2xl max-w-sm w-full"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <div className="p-1.5 rounded-lg bg-accent-primary/10"><Info className="w-4 h-4 text-accent-primary" /></div>
                                    <h3 className="font-bold text-neutral-900 dark:text-neutral-100">{infoModal.title}</h3>
                                </div>
                                <button onClick={() => setInfoModal(null)} className="p-1 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"><X className="w-4 h-4 text-neutral-400" /></button>
                            </div>
                            <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">{infoModal.body}</p>
                            <div className="mt-6 flex justify-end">
                                <button onClick={() => setInfoModal(null)} className="px-4 py-2 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 rounded-xl text-sm font-bold">{t('common.gotIt', 'Got it')}</button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};
